import { type LineItemType } from "legacyComponents/LineItemContainer.types";
import type moment from "moment";
import { stringifyUrl } from "query-string";
import queryString from "query-string";
import { generatePath, matchPath } from "react-router";

import type { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import type { IWebviewRouteParams } from "@btnet/utilities-webview";

import {
    BTLoginTypes,
    CostType,
    DirectionTypes,
    DirectoryType,
    type DiscussionsLinkedTypes,
    EmailFolder,
    EntityTypes,
    type ForbiddenReason,
    HelpCenterTypes,
    MediaPropertiesTab,
    MediaType,
    OpenActivityType,
    OpenedFrom,
    type ReplyType,
    type TabPermissions,
    type TagTypes,
    type ViewingPermissionAction,
} from "types/enum";

import { getCurrentEnvironment } from "utilities/environment/environment";
import { billAddPath } from "utilities/flatRoutes/bill.routes";
import {
    dailyLogAddPath,
    dailyLogEditPath,
    dailyLogViewPath,
} from "utilities/flatRoutes/dailyLog.routes";
import { jobDetailPath } from "utilities/flatRoutes/job.routes";
import { warrantyAddPath } from "utilities/flatRoutes/warranty.routes";
import { type SortDirection } from "utilities/list/list.types";
import { getCurrentPortalType, PortalType } from "utilities/portal/portal";
import { routesWebforms } from "utilities/routesWebforms";
import { removeQueryStringParams } from "utilities/url/url";

import { type IDTypes } from "commonComponents/entity/accounting/common/accounting.api.types";
import { type ScheduleConflictViews } from "commonComponents/entity/scheduleConflicts/ScheduleConflict.api.types";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { type TabOptions } from "entity/bidPackage/BidPackage/BidPackage";
import { type BillSettingsSectionId } from "entity/bill/BillSettings/BillSettingsPresentational";
import { type BudgetSettingsSectionId } from "entity/budget/budgetSettings/budgetSettings.api.types";
import { type CategoryType } from "entity/category/common/categoryType";
import { CommentListTabKeys } from "entity/comment/CommentList/CommentList.api.types";
import { type CostCatalogViewType } from "entity/costCatalog/CostCatalog.types";
import { TemplateType } from "entity/document/Document/Document.api.types";
import { type SystemFilterType } from "entity/filters/Filter/Filter.api.types";
import { type ImportTypes } from "entity/importWizard/ImportWizard/ImportWizard.types";
import {
    type InternalUserLoadAction,
    InternalUserTabKeys,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { JobDetailTab } from "entity/job/Job.api.types";
import { JobListTabs } from "entity/job/JobList/JobList.api.types";
import { getLeadTabUrlSegment, LeadTabTypes } from "entity/lead/Common/LeadTab/LeadTab.types";
import { LeadToJobModes } from "entity/lead/LeadToJob/LeadToJob.types";
import { type IInvalidInviteQSParams } from "entity/LinkAccountActivation/InvalidInvite/RouteInvalidInvitePage";
import { type ExternalIntegrationTypes } from "entity/marketplace/MarketplaceDirectory/MarketplaceDirectory.api.types";
import { type AdminOnlinePaymentReportTabs } from "entity/onlinePayment/AdminOnlinePaymentReports/AdminOnlinePaymentReports.types";
import {
    PurchasingDashboardActivityPages,
    PurchasingDashboardPages,
} from "entity/rebate/PurchasingDashboard/PurchasingDashboard.types";
import {
    type ScheduleEntityDefaultParams,
    type ScheduleTabNames,
} from "entity/schedule/Schedule/Schedule.api.types";
import { type ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";
import { SelectionListContainerTabs } from "entity/selection/SelectionListContainer/SelectionListContainer.types";
import { type SelectionOptionStateOverride } from "entity/selectionOptions/SelectionOption/SelectionOption.types";
import { SubSetupTabKeys } from "entity/sub/SubSetup/SubSetup.api.types";
import { SubTabKeys } from "entity/sub/SubVendor/SubVendor.api.types";
import { SurveyListContainerTabs } from "entity/survey/common/SurveyListContainer/SurveyListContainer.types";
import { type TaskFilterPresetKey } from "entity/task/shared/views/taskFilterPresets";
import { TemplateListTabs } from "entity/template/TemplateList/TemplateListTab/TemplateListTab.api.types";
import { ShiftListContainerTabs } from "entity/timeClock/ShiftList/ShiftListContainer/ShiftListContainer.api.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";

/** makes the page available to subs and owners, if your page is only for builder you do not need to use this
 * @example <DetailPageLayout path={this.getPathForAllPortals("/Entity/:id")} component={MyEntity} />
 */
function getPathForAllPortals(path: string) {
    return `(/Admin)?(/Owner)?(/Subs)?${path}`;
}

function getPathForOwnerPortal(path: string) {
    return `/Owner${path}`;
}

function getPathForSignedInShare(path: string) {
    return `(/Builder)?(/Owner)?(/Subs)?(/Contact)?${path}`;
}

function getPathForAdminPortal(path: string) {
    return `/Admin${path}`;
}

function getPathForBuilderOrSubPortals(path: string) {
    return `(/Subs)?${path}`;
}

function getPathForBuilderOrOwnerPortals(path: string) {
    return `(/Owner)?${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalSubsPage(path: string) {
    return `/subs/share/:builderId${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalOwnerPage(path: string) {
    return `/owner/share/:builderId?${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForInvoiceExternalOwnerPage(path: string) {
    return `/owner/share/${path}`;
}

function getPathForExternalPrint(path: string) {
    return `/printExternal/:builderId${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalLeadsPage(path: string) {
    return `/share/:builderId${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalMicrodepositPageWithShareToken(path: string) {
    return `/share/:shareToken(.{36})${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalMicrodepositPage(path: string) {
    return `/share/:builderId?${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalScheduleConfirmationPage(path: string) {
    return `/share/:builderId${path}`;
}

/**
 * @deprecated Use a relative route in SharedContent.tsx instead.
 */
function getPathForExternalOutboundPaymentPage(path: string) {
    return `/subs/share/:shareToken(.{36})${path}`;
}

export type {
    /**
     * @deprecated Import from '@btnet/utilities-webview' instead
     */
    IWebviewRouteParams,
};

/**
 * CAUTION: All webview routes are served through cache to support offline mode. That means your webviews should NOT have
 * any references to BTJScriptGlobals; those values are injected by our reverse proxy (which will eventually be phased out post SPA),
 * so they may be stale when offline.
 */
function getPathForWebviewPage(path: string) {
    return `(/Admin)?(/Owner)?(/Subs)?/webview/:platform(ios|android)/:mobileVersion${path}`;
}

/**
 * Adds Portal Type string to the start of the url
 * @param path
 * @param portalType
 */
export function addPathForCurrentPortal(path: string) {
    const currentPortal = getCurrentPortalType();
    return addPathForPortal(path, currentPortal);
}

export function addPathForPortal(path: string, loginType: PortalType) {
    let userPortalName = "";

    if (loginType !== PortalType.BUILDER) {
        if (loginType === PortalType.BTADMIN) {
            userPortalName = "/admin";
        } else {
            userPortalName = "/" + PortalType[loginType].toLowerCase();
        }
    }

    return userPortalName + path;
}

function addQueryParams(url: string, params: Record<string, string>) {
    const urlParams = new URLSearchParams();
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            urlParams.append(key, params[key]);
        }
    }
    if (url.includes("?")) {
        url += "&" + urlParams.toString();
    } else {
        url += "?" + urlParams.toString();
    }
    return url;
}

const marketplaceInternal = {
    redirect: "/Marketplace/MarketplaceRedirect",
};

export function getBaseRoute() {
    if (window === window.top) {
        // We aren't in an iframe
        return routes.appBase;
    }
    return "";
}

/**
 *
 * @deprecated Use proper routing instead
 */
export const updateWebformsQueryStringParams = (excludedKeys: string[] = []) => {
    const qsParams = removeQueryStringParams(window.location.search, excludedKeys);
    routesWebforms.replaceWebformsHistoryState(`?${qsParams}`);
};

/**
 * Get the path (everything after .net) from a URL.
 * @param url
 * @returns the path from the URL, or null if url isn't a valid url or if it isn't a BT url
 */
export const getPathFromBTUrl = (url: string) => {
    let urlObj: URL;
    try {
        urlObj = new URL(url);
    } catch (ignored) {
        return null;
    }
    // is this URL inside BT
    if (urlObj.origin !== window.location.origin) return null;
    return urlObj.pathname;
};

// todo see if making a component would make this cleaner, see https://www.blogreader.com.au/blog/named-routes-with-react-router.html
/**
 * Note: If you want to open a legacy webforms page use webformsDialogHandler
 * @see webformsDialogHandler
 */
export const routes = {
    healthcheck: "/healthcheck",
    admin: {
        loginLink: "/api/Admin/Login",
        loginLinkApix: "/apix/v2/admin/auth/login",
        logoutLink: "/api/Admin/Logout",
        logoutLinkApix: "/apix/v2/admin/auth/logout",
        users: {
            usernameLookup: "/Admin/UsernameLookup",
        },
        btAdminUsers: "/Admin/BTAdminUsers",
        devUtilities: {
            appSettings: "/Admin/AppSettings",
        },
        spamEmailRemoval: "/Admin/SpamEmailRemoval",
        subscriptionPricingLimits: {
            list: getPathForAdminPortal("/SubscriptionPricingLimits"),
        },
        adminMain: "/Admin/AdminMain",
        adminSubscriptionManager: {
            subscriptionManager: getPathForAdminPortal(
                "/SubscriptionManager/:builderID/:subscriptionID",
            ),
            getSubscriptionManagerLink(builderID: number, subscriptionID: string) {
                return generatePath(this.subscriptionManager, {
                    builderID,
                    subscriptionID,
                });
            },
        },
        accountingWebhookTesting: "/Admin/AccountingWebhookTesting",
        subscriptionPricingGuardrails: {
            list: "/Admin/SubscriptionPricingLimits",
        },
        builders: "/Admin/Builders",
        getBuildersPath(builderId: number) {
            return `${this.builders}?builderID=${builderId}`;
        },
        impersonate: "/Admin/Impersonate",
        /**
         * Durable deep link into the one-click impersonation flow. Names only *who* to impersonate
         * and *where* to land — the one-time code is minted when the link is opened, so unlike a
         * `/app/ml/{code}` URL this one doesn't expire and is safe to store or share internally.
         */
        getImpersonatePath(targetGlobalUserId: number, redirectPath?: string) {
            const params = new URLSearchParams({
                targetGlobalUserId: String(targetGlobalUserId),
            });
            if (redirectPath) {
                params.set("redirectPath", redirectPath);
            }
            return `${this.impersonate}?${params.toString()}`;
        },
        lowesAccountStatus: "/Admin/LowesAccountStatus",
        notificationServiceTemplates: "/Admin/NotificationServiceTemplates",
        customerFeedback: "/Admin/CustomerFeedback",
    },
    user: {
        linkAccount: "/User/LinkAccount",
        linkAccountActivation: "/User/LinkAccountBuildertrend",
        linkInviteExpired: "/User/LinkInviteExpired",
        linkInvalidInvite: "/User/LinkInvalidInvite",
        linkCrossLoginTypeActivationError: "/User/CrossLoginTypeActivationError",
        linkSignedInAccountActivation: getPathForSignedInShare("/User/LinkSignedInAccount"),
        createAccount: getPathForSignedInShare("/User/CreateAccount"),
        changeEmail: getPathForSignedInShare("/User/ChangeEmail"),
        finalizeActivation: getPathForSignedInShare("/User/FinalizeActivation"),
        newUser: "/User/NewUser",
        postLogin: "/User/PostLogin",
        getPostLogin(stateIdentifier?: string, email?: string, issuer?: string) {
            let path = generatePath(this.postLogin);
            const urlParams = new URLSearchParams();
            if (email) {
                urlParams.append("email", email);
            }
            if (issuer) {
                urlParams.append("issuer", issuer);
            }
            if (stateIdentifier) {
                urlParams.append("stateIdentifier", stateIdentifier);
            }
            path = `${path}?${urlParams.toString()}`;
            return path;
        },
        getFinalizeActivation(shareToken: string, loginType: BTLoginTypes, username: string) {
            if (loginType === BTLoginTypes.CONTACT) {
                loginType = BTLoginTypes.OWNER;
            }
            const portalType = loginType ? `/${BTLoginTypes[loginType].toLowerCase()}` : "";
            const path =
                generatePath(`${routes.share.path}${portalType}${this.finalizeActivation}`, {
                    shareToken,
                }) + `?username=${encodeURIComponent(username)}`;
            return path;
        },
        getLinkAccountLink(shareToken: string) {
            return generatePath(`${routes.share.path}${this.linkAccount}`, { shareToken });
        },
        getLinkAccountActivation(shareToken: string, username: string) {
            let path = generatePath(`${routes.share.path}${this.linkAccountActivation}`, {
                shareToken,
            });
            path = `${path}?username=${username}`;
            return path;
        },
        getLinkSignedInAccountActivation(
            shareToken: string,
            username: string,
            loginType?: BTLoginTypes,
        ) {
            const portalType = loginType ? `/${BTLoginTypes[loginType].toLowerCase()}` : "";
            let path = generatePath(
                `${routes.share.path}${portalType}${this.linkSignedInAccountActivation}`,
                {
                    shareToken,
                },
            );
            path = `${path}?username=${username}`;
            return path;
        },
        getCreateAccountLink(shareToken: string, loginType: BTLoginTypes) {
            const portalType = loginType ? `/${BTLoginTypes[loginType].toLowerCase()}` : "";
            const path = generatePath(`${routes.share.path}${portalType}${this.createAccount}`, {
                shareToken,
            });
            return path;
        },
        getChangeEmailLink(shareToken: string, loginType?: BTLoginTypes) {
            const portalType = loginType ? `/${BTLoginTypes[loginType].toLowerCase()}` : "";
            const path = generatePath(`${routes.share.path}${portalType}${this.changeEmail}`, {
                shareToken,
            });
            return path;
        },
        getNewUserActivation(shareToken: string, username: string) {
            let path = generatePath(`${routes.share.path}${this.newUser}`, { shareToken });
            path = `${path}?username=${username}`;
            return path;
        },
        getLinkInvalidInvite(shareToken: string, qsParams: IInvalidInviteQSParams) {
            let path = generatePath(`${routes.share.path}${this.linkInvalidInvite}`, {
                shareToken,
            });
            const urlParams = new URLSearchParams();
            urlParams.append("loginType", qsParams.loginType.toString());
            if (qsParams.builderName) {
                urlParams.append("builderName", qsParams.builderName.toString());
            }
            if (qsParams.errorType) {
                urlParams.append("errorType", qsParams.errorType.toString());
            }
            if (qsParams.sessionLoginType) {
                urlParams.append("sessionLoginType", qsParams.sessionLoginType.toString());
            }
            path = `${path}?${urlParams.toString()}`;
            return path;
        },
    },
    auth0: {
        redirect: "/auth/redirect",
        getLoginLink(
            domain: string,
            clientId: string,
            host: string,
            audience: string,
            redirectLink: string,
            scopes: string[],
            promptParams: string[],
            externalOrgLinkID?: string,
        ) {
            const scopesUrlString = encodeURIComponent(scopes.join(" "));
            const promptParamsEncoded = encodeURIComponent(promptParams.join(" "));
            const orgLinkParam = externalOrgLinkID ? `&externalOrgLinkID=${externalOrgLinkID}` : "";

            let url = `https://${domain}/authorize?response_type=code&client_id=${clientId}&redirect_uri=https://${host}/app/auth/redirect&audience=${audience}&scope=${scopesUrlString}&state=${redirectLink}${orgLinkParam}`;
            if (promptParamsEncoded) {
                url = url + `&prompt=${promptParamsEncoded}`;
            }

            return url;
        },
        stepUpCallback: "/auth/step-up-callback",
        getStepUpCallbackLink() {
            return `${routes.appBase}${this.stepUpCallback}`;
        },
    },
    account: {
        frozen: "/Frozen",
        frozenExternal: "/frozen-external",
        getFrozenLink(
            isClosed: boolean,
            isAdmin?: boolean,
            builderName?: string,
            encryptedBuilderId?: string,
            contactSupport?: boolean,
        ) {
            const adminQS = `&isAdmin=${isAdmin}`;
            const builderNameQS = `&builderName=${builderName}`;
            const encryptedQS = `&encryptedBuilderId=${encryptedBuilderId}`;
            const mustContactSupport = `&mustContactSupport=${contactSupport}`;
            return `${this.frozen}?isClosed=${isClosed}${isAdmin ? adminQS : ""}${
                contactSupport ? mustContactSupport : ""
            }${builderName ? builderNameQS : ""}${encryptedBuilderId ? encryptedQS : ""}`;
        },
    },
    accountSetup: {
        wizard: "/AccountSetup/Wizard",
    },
    addressConfirmation: {
        details: "/AddressConfirmation",
        getAddressConfirmationLink() {
            return this.details;
        },
    },
    appBase: "/app",
    home: "/",
    bid: {
        list: getPathForAllPortals("/Bids"),
        subsWebview: getPathForWebviewPage("/Bids"),
        webviewDetails: getPathForWebviewPage("/Bid/:id"),
        subsWebviewDetails: getPathForWebviewPage("/Bid/:id/:jobId?/:openedFrom?/:builderId?"),
        print: "/Bid/Print",
        getPrintLink(id: number) {
            const path = `${this.print}?id=${id}`;
            return addPathForCurrentPortal(path);
        },
        getListLink() {
            return addPathForCurrentPortal("/Bids");
        },
        settings: "/BidSettings",
        getSettingsLink() {
            return this.settings;
        },

        details: getPathForBuilderOrSubPortals("/Bid/:id/:jobId/:openedFrom/:builderId"),
        getDetailsLink(
            id: number,
            jobId: number,
            openedFrom = OpenedFrom.Nothing,
            builderId: number = 0,
        ) {
            const path = generatePath(this.details, { id, jobId, openedFrom, builderId });
            return addPathForCurrentPortal(path);
        },

        external: getPathForExternalSubsPage("/Bid/:bidId/:subId/:shareToken(.{36})?"),
        getExternalLink(builderId: string, bidId: string, subId: string, shareToken?: string) {
            return `${generatePath(this.external, { builderId, bidId, subId, shareToken })}`;
        },
    },
    bidPackage: {
        list: "/BidPackages",
        print: "/BidPackage/Print",
        getPrintLink(ids: number[]) {
            const path = `/BidPackage/Print?ids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        getListLink() {
            return addPathForCurrentPortal(this.list);
        },
        webview: getPathForWebviewPage("/BidPackages"),
        detailsWebview: getPathForWebviewPage("/BidPackage/:bidPackageId/:jobId?"),
        details: getPathForBuilderOrSubPortals("/BidPackage/:id/:jobId"),
        getDetailsLink(
            id: number,
            jobId: number,
            isTemplateMode?: boolean,
            initialTab: TabOptions = "general",
        ) {
            const templateModeQSParam =
                typeof isTemplateMode !== "undefined" ? `&isTemplateMode=${isTemplateMode}` : "";
            const path = `${generatePath(this.details, {
                id,
                jobId,
                isTemplateMode,
            })}?initialTab=${initialTab}${templateModeQSParam}`;
            return addPathForCurrentPortal(path);
        },
    },
    branding: {
        settings: "/BrandingSettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    budget: {
        list: getPathForAllPortals("/Budget"),
        legacy: getPathForAllPortals("/Budget/Legacy"),
        getListLink() {
            return addPathForCurrentPortal("/Budget");
        },
        getLegacyLink() {
            return addPathForCurrentPortal("/Budget/Legacy");
        },
        settings: "/BudgetSettings",
        getSettingsLink(sectionId?: BudgetSettingsSectionId) {
            return sectionId ? `${this.settings}#${sectionId}` : this.settings;
        },
    },
    ownerBudget: {
        list: "/Owner/Budget/Legacy",
        getListLink() {
            return this.list;
        },
    },
    builderRiskInsurance: {
        settings: "/RiskInsuranceSettings/:settingsLayout/:autoOpenForm",
        requestForm: "/RiskInsurance/JobsiteRequestForm/:jobId",
        jobRequestForm: "/RiskInsurance/JobRequestForm/:jobId",
        builderProposalForm: "/RiskInsurance/BuilderProposalForm/:builderId",
        getRequestFormLink(jobId: number) {
            return generatePath(this.requestForm, { jobId });
        },
        getJobRequestFormLink(jobId: number) {
            return generatePath(this.jobRequestForm, { jobId });
        },
        getSettingsLink(settingsLayout: boolean, autoOpenForm: boolean) {
            return generatePath(this.settings, { settingsLayout, autoOpenForm });
        },
        getBuilderProposalForm(builderId: number) {
            return generatePath(this.builderProposalForm, { builderId });
        },
        briUpdateUtility: "/Admin/BRIUpdateUtility",
    },
    builderSetup: {
        adminSearch: "/Admin/BuilderSetup",
        adminSetup: "/Admin/BuilderSetup/:builderId",
        getAdminSetupLink(builderId: string) {
            return generatePath(this.adminSetup, { builderId });
        },
        setup: "/setup",
        webview: getPathForWebviewPage("/Setup"),
        uploadFiles: "/UploadFiles",
    },
    takeoff: {
        takeoffSettings: "/TakeoffSettings",
        getTakeoffSettingsLink() {
            return this.takeoffSettings;
        },
        launchTakeoff: "/LaunchTakeoff",
        getLaunchTakeoffLink() {
            return this.launchTakeoff;
        },
    },
    bulkExport: {
        settings: "/BulkExportSettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    changeOrder: {
        print: getPathForAllPortals("/ChangeOrder/Print"),
        getPrintLink(ids: number[]) {
            const path = `/ChangeOrder/Print?ids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        list: getPathForAllPortals("/ChangeOrders"),
        getListLink() {
            return addPathForCurrentPortal("/ChangeOrders");
        },
        getListLinkForBTAdminImpersonation() {
            return "/ChangeOrders";
        },
        webview: getPathForWebviewPage("/ChangeOrders"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        settings: "/ChangeOrderSettings",
        getSettingsLink() {
            return this.settings;
        },
        changeOrderPaymentDetails: getPathForAllPortals("/Payments/:id/ChangeOrderPayment"),
        getChangeOrderPaymentDetailsLink(id: number) {
            const path = generatePath(this.changeOrderPaymentDetails, { id });
            return addPathForCurrentPortal(path);
        },
        ownerRequest: "/Owner/ChangeOrders/:id/:jobId/Request",
        ownerRequestWebview: getPathForWebviewPage("/ChangeOrders/:id/:jobId/Request"),
        getOwnerRequestLink(id: number, jobId: number) {
            const path = generatePath(this.ownerRequest, { id, jobId });
            return path;
        },
        changeOrderApprovalSuccess: getPathForAllPortals("/ChangeOrder/ApprovalSuccess"),
        getChangeOrderApprovalSuccessLink() {
            const path = generatePath(this.changeOrderApprovalSuccess);
            return addPathForCurrentPortal(path);
        },
        changeOrderDetails: getPathForAllPortals("/ChangeOrder/:id/:jobId"),
        getChangeOrderDetailsPath(id: number, jobId: number) {
            return generatePath(this.changeOrderDetails, { id, jobId });
        },
        // Route needs "/Details" at the end to differentiate between the legacy route and new full page route
        changeOrderDetailsPage: getPathForAllPortals("/ChangeOrders/:id/:jobId/Details"),
        changeOrderDetailsPageWebview: getPathForWebviewPage("/ChangeOrders/:id/:jobId/Details"),
        getChangeOrderDetailsFullPage(
            id: number,
            jobId: number,
            platform?: string,
            mobileVersion?: string,
        ) {
            if (platform && mobileVersion) {
                const path = generatePath(this.changeOrderDetailsPageWebview, {
                    id,
                    jobId,
                    platform,
                    mobileVersion,
                });
                return addPathForCurrentPortal(path);
            }
            const path = generatePath(this.changeOrderDetailsPage, { id, jobId });
            return addPathForCurrentPortal(path);
        },
        getChangeOrderDetailsLink(id: number, jobId: number, fromRfi?: boolean) {
            let path = generatePath(this.changeOrderDetails, { id, jobId });
            if (fromRfi) {
                path += "?fromRFI=true";
            }
            return addPathForCurrentPortal(path);
        },
        bidMapping() {
            return "/BidMapping";
        },
        bidPackageDetails() {
            return "/BidPackage/:bidPackageId/:jobId";
        },
        getBidPackageDetailsLink(bidPackageId: number, jobId: number) {
            return generatePath(this.bidPackageDetails(), { bidPackageId, jobId });
        },
        bidDetails() {
            return `/Bid/:bidId/:jobId/${OpenedFrom.ChangeOrder}`;
        },
        getBidDetailsLink(jobId: number, bidId: number) {
            return generatePath(this.bidDetails(), { jobId, bidId });
        },
    },
    clientUpdates: {
        list: "/ClientUpdates",
        getListLink() {
            return this.list;
        },
        webview: getPathForWebviewPage("/ClientUpdates"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            queryParams?: {
                jobId?: number;
            },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );

            if (!queryParams) {
                return path;
            }

            const params = new URLSearchParams();
            if (queryParams.jobId) {
                params.append("jobId", queryParams.jobId.toString());
            }

            const queryString = params.toString();
            return queryString ? `${path}?${queryString}` : path;
        },
        details: "/ClientUpdate/:id",
        getDetailsLink(id: number) {
            return generatePath(this.details, { id });
        },
        getFullDetailsLink(id: number, loginType: BTLoginTypes) {
            if (loginType === BTLoginTypes.CONTACT) {
                return `${routes.ownerSummary.getLink()}${this.getDetailsLink(id)}`;
            } else if (loginType === BTLoginTypes.BUILDER) {
                return addPathForPortal(
                    `${this.getListLink()}${this.getDetailsLink(id)}`,
                    PortalType.BUILDER,
                );
            } else {
                throw new Error(`Invalid login type: ${loginType} for client update details link`);
            }
        },
        print: getPathForAllPortals("/ClientUpdate/Print"),
        getPrintLink(ids: number[]) {
            return addPathForCurrentPortal(generatePath(this.print)) + `?ids=${ids.join(",")}`;
        },
        settings: "/ClientUpdateSettings",
        getSettingsLink() {
            return this.settings;
        },
        webviewSettings: getPathForWebviewPage("/ClientUpdates/ClientUpdateSettings"),
        getWebviewSettingsLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewSettings, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        webviewDetails: getPathForWebviewPage("/ClientUpdates/ClientUpdate/:id"),
        getWebviewDetailsLink(
            id: number,
            webviewConfig: IWebviewRouteParams,
            queryParams?: {
                jobId?: number;
            },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    id,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );

            if (!queryParams) {
                return path;
            }

            const params = new URLSearchParams();
            if (queryParams.jobId) {
                params.append("jobId", queryParams.jobId.toString());
            }

            const queryString = params.toString();
            return queryString ? `${path}?${queryString}` : path;
        },
        webviewPrint: getPathForWebviewPage("/ClientUpdates/Print"),
        getWebviewPrintLink(webviewConfig: IWebviewRouteParams, ids: number[]) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewPrint, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
            return `${path}?ids=${ids.join(",")}`;
        },
    },
    deposits: {
        list: getPathForAllPortals("/DepositsList"),
        webviewList: getPathForWebviewPage("/DepositsList"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        details: getPathForBuilderOrOwnerPortals("/Deposit/:id/:jobId"),
        checkoutBuilder: addPathForPortal("/Deposit/:id/:jobId/checkout", PortalType.BUILDER),
        checkoutOwner: addPathForPortal("/Deposit/:id/:jobId/checkout", PortalType.OWNER),
        checkoutExternal: "/share/:shareToken/Deposit/checkout",
        getCheckoutBuilderPath(id: number, jobId: number) {
            return `${routes.appBase}${generatePath(this.checkoutBuilder, { id, jobId })}`;
        },
        getCheckoutOwnerPath(id: number, jobId: number) {
            return `${routes.appBase}${generatePath(this.checkoutOwner, { id, jobId })}`;
        },
        getCheckoutExternalPath(shareToken: string) {
            return `${routes.appBase}${generatePath(this.checkoutExternal, { shareToken })}`;
        },
        getListLink() {
            return addPathForCurrentPortal("/DepositsList");
        },
        getDetailsPath(id: number, jobId: number, payOnline: boolean = false) {
            return generatePath(this.details, {
                id,
                jobId,
                payOnline,
            });
        },
        getDetailsLink(depositId: number, jobId: number, payOnline: boolean = false) {
            return addPathForCurrentPortal(this.getDetailsPath(depositId, jobId, payOnline));
        },
        externalWebview: getPathForWebviewPage("/DepositExternal/:shareToken"),
        external: getPathForExternalOwnerPage("/Deposit/:depositId/:shareToken(.{36})?"),
        getExternalLink(depositId: string, builderId: string, shareToken: string) {
            return `${generatePath(this.external, {
                depositId,
                builderId,
                shareToken,
            })}`;
        },
        getListLinkWithDepositSelected(
            depositId: number,
            jobId: number,
            payOnline: boolean = false,
            systemFilterType?: SystemFilterType,
        ) {
            let url = addPathForCurrentPortal(
                "/DepositsList" + this.getDetailsPath(depositId, jobId, payOnline),
            );
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        postDepositCheckout: "/PostDepositCheckout/:depositId",
        getPostDepositCheckoutPath(depositId: number) {
            return generatePath(this.postDepositCheckout, { depositId });
        },
        sendToPos: "/SendToPos",
        getSendToPosPath() {
            return this.sendToPos;
        },
        payOnline: "/payDepositOnline",
        getPayOnlinePath() {
            return this.payOnline;
        },
        payOffline: "/payOffline",
        getPayOfflinePath() {
            return this.payOffline;
        },
        applyToInvoice: "/ApplyToInvoice/:jobId/:id",
        getApplyToInvoicePath(jobId: number, id: number) {
            return generatePath(this.applyToInvoice, { jobId, id });
        },
        printWebview: getPathForWebviewPage("/Deposit/Print/:depositId/:jobId"),
        print: getPathForAllPortals("/Deposit/Print"),
        getPrintLink(jobId: number, depositId: number) {
            const path = `/Deposit/Print?jobId=${jobId}&depositId=${depositId}`;
            return addPathForCurrentPortal(path);
        },
        externalPrintShareToken: `/Deposit/printExternal/:shareToken(.{36})`,
        getExternalPrintLinkShareToken(shareToken: string) {
            return generatePath(this.externalPrintShareToken, {
                shareToken,
            });
        },
        webview: getPathForWebviewPage("/Deposit/:depositId/:jobId"),
        externalDepositPrintLink: `/Deposit/printExternal/:depositId/:jobId`,
        getExternalDepositPrintLink(depositId: number, jobId: number) {
            const path = `/Deposit/Print?jobId=${jobId}&depositId=${depositId}`;
            return addPathForCurrentPortal(path);
        },
    },
    editor: {
        /**
         * Clean this up once mobile updates the firbase config to use the new editor route.
         * TODO also remove /editor from precacheWorker.
         * @deprecated this should be removed after mobile moves to the webview/ route */
        legacyMobile:
            "/editor/:viewMode(fullscreen|embed)/:platform(ios|android)/:interfaceVersion/:mobileVersion",

        mobile: getPathForWebviewPage("/editor/:viewmode(fullscreen|embed)/:interfaceVersion"),
        getMobileEditorLink(
            viewMode: "fullscreen" | "embed",
            platform: "ios" | "android",
            interfaceVersion: string,
            mobileVersion: string,
        ) {
            return generatePath(this.legacyMobile, {
                viewMode,
                platform,
                interfaceVersion,
                mobileVersion,
            });
        },
    },
    specification: {
        base: getPathForAllPortals("/Specifications"),
        getBaseLink() {
            return addPathForCurrentPortal("/Specifications");
        },
        list: getPathForAllPortals("/Specifications/List"),
        getListLink() {
            return addPathForCurrentPortal("/Specifications/List");
        },
        book: getPathForAllPortals("/Specifications/Book"),
        bookWithSpec: getPathForAllPortals("/Specifications/Book/View/:externalId"),
        getBookLink(id?: string) {
            if (id) {
                return addPathForCurrentPortal(generatePath(this.bookWithSpec, { externalId: id }));
            }
            return addPathForCurrentPortal(generatePath(this.book));
        },
        details: "/Specification/:id/:jobId",
        getSpecificationDetails(id: number, jobId: number) {
            return generatePath(this.details, { id, jobId });
        },
        openConversation: getPathForAllPortals("/Specifications/:specId/:jobId/Conversation"),
        getOpenConversationLink(specId: number, jobId: number) {
            const path = generatePath(this.openConversation, { specId, jobId });
            return addPathForCurrentPortal(path);
        },
        print: getPathForAllPortals("/Specifications/Print"),
        getPrintLink(ids: number[]) {
            const path = `/Specifications/Print?specificationIds=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        webview: getPathForWebviewPage("/:interfaceVersion/Specifications/Book"),
        webviewWithSpec: getPathForWebviewPage(
            "/:interfaceVersion/Specifications/Book/View/:externalId",
        ),
        getWebviewLink(
            webviewConfig: IWebviewRouteParams,
            interfaceVersion: string,
            queryParams?: { jobId?: string },
            externalId?: string,
        ) {
            // Build path with optional spec ID (external ID)
            const pathParams: Record<string, string> = {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                interfaceVersion,
            };

            // Use appropriate route pattern based on whether spec is selected
            const routePattern = externalId ? this.webviewWithSpec : this.webview;
            if (externalId) {
                pathParams.externalId = externalId;
            }

            const path = addPathForCurrentPortal(generatePath(routePattern, pathParams));

            // Add jobId query param if provided
            if (queryParams?.jobId) {
                return `${path}?jobIds=${queryParams.jobId}`;
            }
            return path;
        },
        // Relative paths for RouteRelative within /app/share/{token} context
        external: "/Specifications/External",
        externalWithSpec: "/Specifications/External/View/:externalId",
        getExternalLink(shareToken: string, externalId?: string) {
            if (externalId) {
                return `${routes.share.getLink(shareToken)}${generatePath(this.externalWithSpec, {
                    externalId,
                })}`;
            }
            return `${routes.share.getLink(shareToken)}${this.external}`;
        },
        // Relative path (mounted under the List/Book route) for the "Add from catalog" import modal.
        addFromCatalog: "/Add/Catalog",
        getAddFromCatalogLink() {
            return this.addFromCatalog;
        },
    },
    company: {
        settings: "/CompanySettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    document: {
        create: "/Documents/Create/:folderId/:builderId/:jobId/",
        getCreateLink(folderId: number, builderId: number, jobId: number) {
            return generatePath(this.create, { folderId, builderId, jobId });
        },

        list: getPathForAllPortals("/Documents/:directoryType/:folderId"),
        getAdminSetupListLink() {
            const path = `/Documents/${DirectoryType.GlobalDocs}/0/`;
            return routes.jobPicker.selectJobLink(JobIdTypes.GlobalDocs, path);
        },
        getListLink(
            directoryType: DirectoryType = DirectoryType.Standard,
            folderId: number = 0,
            selectedJobPickerId?: number,
        ) {
            let path = addPathForCurrentPortal(
                generatePath(`/Documents/${DirectoryType[directoryType]}/:folderId`, {
                    directoryType,
                    folderId,
                }),
            );
            if (selectedJobPickerId) {
                path = routes.jobPicker.selectJobLink(selectedJobPickerId, path);
            }
            return path;
        },

        unread: "/Owner/Document/Unread",
        getUnreadLink() {
            return this.unread;
        },
        editOnline: "/EditOnline/:documentInstanceId",
        getEditOnlineLink(documentInstanceId: number) {
            return generatePath(this.editOnline, { documentInstanceId });
        },

        webview: getPathForWebviewPage("/Documents/:directoryType/:folderId"),
        unsignedDocumentsWebview: getPathForWebviewPage("/UnsignedDocuments"),
    },
    gettingStarted: {
        dataEntry: "/GettingStarted/DataEntry",
        getDataEntryLink() {
            return this.dataEntry;
        },
    },
    job: {
        list: getPathForAllPortals("/Jobs/List"),
        map: getPathForAllPortals("/Jobs/Map"),
        getListLink(tab: JobListTabs = JobListTabs.List): string {
            return addPathForCurrentPortal(generatePath(`/Jobs/${JobListTabs[tab]}`, { tab }));
        },
        comments: "/Jobs/Comments",
        webview: getPathForWebviewPage("/Jobs/Comments"),
        getCommentsLink() {
            return this.comments;
        },
        print: getPathForAllPortals("/JobPriceSummary/Print/:jobId/:printOnLoad?"),
        getPrintLink(jobId: number, printOnLoad?: boolean) {
            const path = `/JobPriceSummary/Print/${jobId}/${printOnLoad}`;
            return addPathForCurrentPortal(path);
        },
        jobPrint: getPathForAllPortals("/JobPrint/Print/:jobId/:printOnLoad?"),
        getJobPrintLink(jobId: number, printOnLoad: boolean) {
            const path = `/JobPrint/Print/${jobId}/${printOnLoad}`;
            return addPathForCurrentPortal(path);
        },
        details: getPathForAllPortals(
            "/Job/:jobId/:defaultTab/:accessedFromContact/:accessedFromLead/:ownerInvoiceId/:openCondensed?",
        ),
        getDetailsPath(
            jobId: number,
            defaultTab: JobDetailTab = JobDetailTab.Job,
            accessedFromContact: boolean = false,
            accessedFromLead: boolean = false,
            ownerInvoiceId?: number | null,
            openCondensed: boolean = false,
        ) {
            const ownerInvoiceParam =
                typeof ownerInvoiceId === "undefined" ||
                ownerInvoiceId === null ||
                isNaN(ownerInvoiceId)
                    ? "undefined"
                    : ownerInvoiceId;
            return generatePath(this.details, {
                jobId,
                defaultTab,
                accessedFromContact,
                accessedFromLead,
                ownerInvoiceId: ownerInvoiceParam,
                openCondensed,
            });
        },
        getDetailsLink(
            jobId: number,
            defaultTab: JobDetailTab = JobDetailTab.Job,
            accessedFromContact: boolean = false,
            accessedFromLead: boolean = false,
            ownerInvoiceId?: number | null,
            openCondensed: boolean = false,
        ) {
            const path = this.getDetailsPath(
                jobId,
                defaultTab,
                accessedFromContact,
                accessedFromLead,
                ownerInvoiceId,
                openCondensed,
            );
            return addPathForCurrentPortal(path);
        },
        jobFromTemplate: "/JobFromTemplate/:leadId/:jobType/:leadSoldDate/:sourceTemplateId",
        getJobFromTemplateLink(
            leadId: number,
            jobType: number,
            leadSoldDate: string,
            sourceTemplateId: number = JobIdTypes.NoJobs,
        ) {
            return generatePath(this.jobFromTemplate, {
                leadId,
                jobType,
                leadSoldDate,
                sourceTemplateId,
            });
        },
        settings: "/JobSettings",
        getSettingsLink() {
            return this.settings;
        },
        jobGroupEditor: "/Job/JobGroupEditor/:groupId",
        getJobGroupEditorLink(groupId: number) {
            return generatePath(this.jobGroupEditor, { groupId: groupId });
        },
        jobFinancials: getPathForAllPortals("/JobFinancials"),
        jobFinancialsWebview: getPathForWebviewPage("/JobFinancials"),
        getJobFinancialsLink() {
            return addPathForCurrentPortal("/JobFinancials");
        },
        clientPortalSettings: getPathForAllPortals("/ClientPortalSettings"),
        getClientPortalSettings() {
            const path = generatePath(this.clientPortalSettings);
            return path;
        },
        jobPriceSummaryPrintWebView: getPathForWebviewPage("/JobPriceSummaryPrint/:jobId"),
        webviewDetails: getPathForWebviewPage(jobDetailPath),
        webviewList: getPathForWebviewPage("/Jobs/List"),
    },
    jobPicker: {
        selectJob: getPathForAllPortals("/JobPicker/SelectJob"),
        selectJobLink(jobId: number, nextUrl: string, portalTypeOverride?: PortalType) {
            if (portalTypeOverride) {
                return generatePath(
                    addPathForPortal(
                        `${this.selectJob}?selectJobId=${jobId}&nextUrl=${encodeURIComponent(
                            nextUrl,
                        )}`,
                        portalTypeOverride,
                    ),
                );
            }
            return generatePath(
                addPathForCurrentPortal(
                    `${this.selectJob}?selectJobId=${jobId}&nextUrl=${encodeURIComponent(nextUrl)}`,
                ),
            );
        },
    },
    mobile: {
        mobileLogin: "/MobileLogin",
        mobileLoginByCode: "/ml/:code",
        mobileLoginByCodeAuth: "/MagicMobileLogin/:code",
        mobileDownload: "/mobile/download",
        getMobileDownload() {
            return this.mobileDownload;
        },
        /**
         * Interstitial the EmailLinkClicked API redirects mobile users to for deep-linkable
         * link types. Attempts the `buildertrend://` deep link and falls back to the app store
         * or the web destination.
         *
         * The page receives the original email link's query string and rebuilds both
         * destinations from the literal prefixes below. It must never be handed a ready-made
         * URL to navigate to: the route is directly reachable, so a `deepLink`/`continueUrl`
         * parameter would let any caller drive `window.location.assign` — including at a
         * `javascript:` URI, which is XSS rather than a mere open redirect.
         */
        emailLinkRedirect: "/EmailLinkRedirect",
        getEmailLinkRedirect() {
            return this.emailLinkRedirect;
        },
        /**
         * Deep link prefix for the interstitial, and the single spelling of the app's URL
         * scheme in this file. Kept whole rather than composed from a separate scheme
         * constant: the literalness is the security property, since it is what makes the host
         * unchangeable by any interstitial parameter.
         *
         * Takes the already-decrypted keys as scalars rather than reusing the page's raw
         * query: by the time the server renders the interstitial the keys are plaintext, and
         * an `&encr=True` link's raw query still holds the *encrypted* values, which the
         * native app cannot resolve.
         *
         * `key3` is intentionally absent — the dispatcher's deep link has never carried it
         * (see `GetRedirectToMobileLink` in the legacy `EmailLinkClicked.aspx.vb`).
         */
        emailLinkRedirectDeepLinkPrefix: "buildertrend://buildertrend.com/?",
        getEmailLinkDeepLink(elt: string, key1: string, key2?: string | null) {
            return (
                `${this.emailLinkRedirectDeepLinkPrefix}elt=${encodeURIComponent(elt)}` +
                `&key1=${encodeURIComponent(key1)}` +
                (key2 ? `&key2=${encodeURIComponent(key2)}` : "")
            );
        },
        /**
         * Same-origin path back into the dispatcher. Because the prefix is a literal absolute
         * path, the result is same-origin by construction — the forwarded query cannot escape
         * to another host or scheme.
         *
         * The query is passed through untouched. The server has already put `skipMobile=true`
         * in it, so the whole suppress-mobile-on-the-second-pass protocol stays on the side
         * that defines it and this page needs no knowledge of it.
         */
        emailLinkClickedApixPrefix: "/apix/v1/EmailLinkClicked?",
        getEmailLinkContinueOnWeb(query: string) {
            return `${this.emailLinkClickedApixPrefix}${query}`;
        },
    },
    notFound: {
        details: "/NotFound",
        getDetailsLink() {
            return generatePath(this.details);
        },
    },
    purchaseOrder: {
        list: getPathForAllPortals("/PurchaseOrders"),
        getListLink() {
            return addPathForCurrentPortal("/PurchaseOrders");
        },
        webview: getPathForWebviewPage("/PurchaseOrders"),
        webviewDetails: getPathForWebviewPage("/PurchaseOrder/:id/:jobId"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        getWebviewDetailsLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    id,
                    jobId,
                }),
            );
        },
        print: getPathForAllPortals("/PurchaseOrder/Print"),
        // This "external" print is really just used by pubsub to generate pdfs of approved POs
        printExternal: getPathForAllPortals(
            getPathForExternalPrint("/PurchaseOrder/Print/:shareToken(.{36})?"),
        ),
        getPrintLink(ids: number[]) {
            const path = `/PurchaseOrder/Print?poid=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        getPrintExternalLink(builderId: string, shareToken: string, isPDFMode: boolean) {
            let path = generatePath(this.printExternal, { builderId, shareToken });
            path = `${path}?isPDFMode=${isPDFMode}`;
            return path;
        },
        details: getPathForAllPortals("/PurchaseOrder/:id/:jobId/:varianceChangeOrderId(\\d+)?"),
        getDetailsPath(id: number, jobId?: number, varianceChangeOrderId?: number) {
            return generatePath(this.details, {
                id,
                jobId,
                varianceChangeOrderId,
            });
        },
        getDetailsLink(id: number, jobId?: number, varianceChangeOrderId?: number) {
            return addPathForCurrentPortal(this.getDetailsPath(id, jobId, varianceChangeOrderId));
        },
        modalPath: "/PurchaseOrder/:id/:jobId/:varianceChangeOrderId(\\d+)?",
        getModalLink(id: number, jobId?: number, varianceChangeOrderId?: number) {
            return generatePath(this.details, {
                id,
                jobId,
                fromAmended: false,
                varianceChangeOrderId,
            });
        },
        changeOrder: getPathForAllPortals(
            "/PurchaseOrder/:id/:jobId/ChangeOrder/:changeOrderId/:jobId",
        ),
        getChangeOrderLink(id: number, jobId: number, changeOrderId: number) {
            const path = generatePath(
                "/PurchaseOrder/:id/:jobId/ChangeOrder/:changeOrderId/:jobId",
                { id, jobId, changeOrderId },
            );
            return addPathForCurrentPortal(path);
        },
        external: getPathForExternalSubsPage("/PurchaseOrder/:poId/:subId/:shareToken(.{36})?"),
        getExternalLink(builderId: string, poId: string, subId: string, shareToken?: string) {
            return generatePath(this.external, { builderId, poId, subId, shareToken });
        },
        approvedPODetails: getPathForAllPortals(
            "/ApprovedPurchaseOrder/:id/:jobId/:varianceChangeOrderId(\\d+)?",
        ),
        getApprovedPODetailsPath(id: number, jobId?: number, varianceChangeOrderId?: number) {
            return generatePath(this.approvedPODetails, {
                id,
                jobId,
                varianceChangeOrderId,
            });
        },
        settings: "/PurchaseOrderSettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    // Builder-portal only (AiaPurchaseOrder feature). Plain path registers no /Owner or /Subs
    // variant, so those portals can't resolve the route at all.
    scheduleOfValues: {
        list: "/ScheduleOfValues",
        getListLink() {
            return addPathForCurrentPortal("/ScheduleOfValues");
        },
        details: "/ScheduleOfValues/:jobId",
        getDetailsLink(jobId: number) {
            return addPathForCurrentPortal(`/ScheduleOfValues/${jobId}`);
        },
    },
    purchaseOrderPayment: {
        list: getPathForAllPortals("/PurchaseOrderPayments"),
        getListLink() {
            return addPathForCurrentPortal("/PurchaseOrderPayments");
        },
        details:
            "/PurchaseOrderPayment/:id/:jobId/:isFromMakeAPayment(true|false)?/:lienWaiver(lienWaiver)?",
        getDetailsLink(
            id: number,
            jobId: number,
            isFromMakeAPayment?: boolean,
            lienWaiver?: string,
        ) {
            return generatePath(
                "/PurchaseOrderPayment/:id/:jobId/:isFromMakeAPayment(true|false)?/:lienWaiver(lienWaiver)?",
                { id, jobId, isFromMakeAPayment, lienWaiver },
            );
        },
    },
    bill: {
        list: getPathForAllPortals("/Bills/:view(InReview)?"),
        getListLink(view?: string) {
            const basePath = "/Bills";
            const fullPath = view ? `${basePath}/${view}` : basePath;
            return addPathForCurrentPortal(fullPath);
        },
        // New unified bills experience (list + detail). Built alongside the legacy
        // /Bills list; will take over /Bills once it fully replaces BillList.
        page: getPathForAllPortals("/BillsInbox"),
        getPageLink() {
            return addPathForCurrentPortal("/BillsInbox");
        },
        getListLinkForBTAdminImpersonation(view?: string) {
            return view ? `/Bills/${view}` : "/Bills";
        },
        approvalQueue: getPathForAllPortals("/bill-review-queue"),
        getApprovalQueueLink() {
            return addPathForCurrentPortal("/bill-review-queue");
        },
        webview: getPathForWebviewPage("/Bills"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        webviewApprovalQueue: getPathForWebviewPage("/bill-review-queue"),
        getWebviewApprovalQueueLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewApprovalQueue, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        webviewDetails: getPathForWebviewPage("/Bill/:billId/:jobId/:lienWaiver(lienWaiver)?"),
        getWebviewDetailsLink(
            webviewConfig: IWebviewRouteParams,
            billId: number,
            jobId: number,
            lienWaiver?: string,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    billId,
                    jobId,
                    lienWaiver,
                }),
            );
        },
        print: getPathForAllPortals("/bill/print"),
        getPrintLink(ids: number[], jobId: number) {
            const path = `/bill/print?billIds=${ids.join(",")}&jobId=${jobId}`;
            return addPathForCurrentPortal(path);
        },
        details: getPathForAllPortals("/Bill/:id/:jobId/:lienWaiver(lienWaiver)?"),
        getDetailsPath(billId: number, jobId: number, lienWaiver?: string) {
            return generatePath("/Bill/:id/:jobId/:lienWaiver(lienWaiver)?", {
                id: billId,
                jobId,
                lienWaiver,
            });
        },
        getDetailsLink(billId: number, jobId: number, lienWaiver?: string) {
            return addPathForCurrentPortal(this.getDetailsPath(billId, jobId, lienWaiver));
        },
        getDetailLinkForContext(
            billId: number,
            jobId: number,
            webviewConfig: IWebviewRouteParams | undefined,
        ) {
            return webviewConfig
                ? this.getWebviewDetailsLink(webviewConfig, billId, jobId)
                : this.getDetailsLink(billId, jobId);
        },
        addPage: getPathForAllPortals(billAddPath),
        getAddPageLink(jobId: number) {
            return addPathForCurrentPortal(generatePath(billAddPath, { jobId }));
        },
        // Relative path for the split-allocation modal nested inside the already-open Bill modal
        // (ado-286470) - combine with `match.url +`.
        splitAllocationModal: "/SplitAllocation",
        // Relative path for the cost-record modal nested inside the already-open Bill modal
        // (ado-286470), same idea as splitAllocationModal above. Unlike splitAllocationModal,
        // this needs a :childBillId param since a bill can have several cost records - combine
        // with `match.url +` and interpolate via getPath.
        costRecordModal: {
            path: "/CostRecord/:childBillId",
            getPath(childBillId: number) {
                return generatePath(this.path, { childBillId });
            },
        },
        mapping: getPathForAllPortals("/BillMapping/:purchaseOrderId/:jobId/:isLinkBillToPO"),
        getMappingPath(purchaseOrderId: number, jobId: number, isLinkBillToPO: boolean = false) {
            return generatePath(this.mapping, {
                purchaseOrderId,
                jobId,
                isLinkBillToPO,
            });
        },
        massPay: "/BillMassPay/:isOnlinePayment/:selectedJobId?",
        getMassPayLink(isOnlinePayment: boolean, selectedJobId?: number) {
            return `${generatePath(this.massPay, { isOnlinePayment, selectedJobId })}`;
        },
        lienWaiverReview: getPathForAllPortals("/LienWaiverReview"),
        lienWaiverReviewExternal: getPathForExternalSubsPage(
            "/LienWaiverReview/:subId/:shareToken(.{36})?",
        ),
        getLienWaiverReviewExternalLink(builderId: string, subId: string, shareToken?: string) {
            return generatePath(this.lienWaiverReviewExternal, {
                builderId,
                subId,
                shareToken,
            });
        },
        settings: "/BillSettings",
        getSettingsLink(sectionId?: BillSettingsSectionId) {
            return sectionId ? `${this.settings}#${sectionId}` : this.settings;
        },
    },
    billPayment: {
        details: getPathForAllPortals("/BillPayment/:id"),
        getDetailsPath(merchantPaymentId: number) {
            return generatePath("/BillPayment/:id", {
                id: merchantPaymentId,
            });
        },
        detail: getPathForAllPortals("/BillPayment/detail/:billPaymentId"),
        getDetailByParentBillPaymentIdPath(parentBillPaymentId: number) {
            return generatePath("/BillPayment/detail/:billPaymentId", {
                billPaymentId: parentBillPaymentId.toString(),
            });
        },
    },
    outboundPayments: {
        walletInfo: "/Wallet",
        mailCheck: "/MailCheck",
        printCheck: "/PrintCheck",
        external: getPathForExternalOutboundPaymentPage("/OutboundPaymentExternal"),
        setup: getPathForExternalOutboundPaymentPage("/AccountSetup"),
        getOutboundPaymentExternalLink(
            shareToken: string,
            fromEmail?: boolean,
            download?: boolean,
        ) {
            let path = generatePath(this.external, { shareToken });
            if (fromEmail) {
                path += `?fromEmail=${fromEmail}`;
            }
            if (download) {
                path += `${fromEmail ? "&" : "?"}download=${download}`;
            }
            return path;
        },
        getAccountSetupLink(shareToken: string) {
            return generatePath(this.setup, { shareToken });
        },
    },
    warranty: {
        print: getPathForAllPortals("/Warranty/Print"),
        getPrintLink(ids: number[]) {
            const path = `/Warranty/Print?ids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },

        list: getPathForAllPortals("/Warranties"),
        getListLink() {
            return addPathForCurrentPortal("/Warranties");
        },

        webview: getPathForWebviewPage("/:interfaceVersion/Warranties"),
        webviewDetails: getPathForWebviewPage("/:interfaceVersion/Warranty/:id/:jobId"),

        details: getPathForAllPortals("/Warranty/:id/:jobId"),
        getDetailsLink(id: number, jobId: number) {
            const path = generatePath("/Warranty/:id/:jobId", { id, jobId });
            return addPathForCurrentPortal(path);
        },

        addPage: getPathForAllPortals(warrantyAddPath),
        getAddPageLink(jobId: number) {
            return addPathForCurrentPortal(generatePath(warrantyAddPath, { jobId }));
        },

        webviewAddPage: getPathForWebviewPage(warrantyAddPath),
        getWebviewAddPageLink(webviewConfig: IWebviewRouteParams, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewAddPage, {
                    jobId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        webviewDetailsPage: getPathForWebviewPage("/Warranty/:id/:jobId"),
        getWebviewDetailsLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetailsPage, {
                    id,
                    jobId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },

        service: getPathForAllPortals("/Warranty/:id/:jobId/Service/:serviceId/:completed"),
        getServiceLink(id: number, jobId: number, serviceId: number, completed: boolean) {
            const path = generatePath("/Warranty/:id/:jobId/Service/:serviceId/:completed", {
                id,
                jobId,
                serviceId,
                completed,
            });
            return addPathForCurrentPortal(path);
        },

        serviceRelative: getPathForAllPortals("/Service/:serviceId/:completed"),
        getServiceLinkRelative(serviceId: number, completed: boolean) {
            const path = generatePath(this.serviceRelative, { serviceId, completed });
            return addPathForCurrentPortal(path);
        },

        servicePage: getPathForAllPortals(
            "/WarrantyService/:warrantyId/:jobId/:serviceId/:completed",
        ),
        getServicePageLink(
            warrantyId: number,
            jobId: number,
            serviceId: number,
            completed: boolean,
        ) {
            const path = generatePath("/WarrantyService/:warrantyId/:jobId/:serviceId/:completed", {
                warrantyId,
                jobId,
                serviceId,
                completed,
            });
            return addPathForCurrentPortal(path);
        },

        webviewServicePage: getPathForWebviewPage(
            "/WarrantyService/:warrantyId/:jobId/:serviceId/:completed",
        ),
        getWebviewServicePageLink(
            webviewConfig: IWebviewRouteParams,
            warrantyId: number,
            jobId: number,
            serviceId: number,
            completed: boolean,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webviewServicePage, {
                    warrantyId,
                    jobId,
                    serviceId,
                    completed,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },

        settings: "/WarrantySettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    submittal: {
        list: getPathForAllPortals("/Submittals"),
        getListLink(systemFilterType?: SystemFilterType) {
            let url = addPathForCurrentPortal("/Submittals");
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        details: getPathForAllPortals("/Submittal/:id/:jobId"),
        getDetailsLink(id: string, jobId: number) {
            const path = generatePath(this.details, { id, jobId });
            return addPathForCurrentPortal(path);
        },
        webview: getPathForWebviewPage("/Submittals"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return generatePath(this.webview, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
            });
        },
        webviewDetails: getPathForWebviewPage("/Submittal/:submittalId/:jobId"),
        getWebviewDetailsLink(
            webviewConfig: IWebviewRouteParams,
            submittalId: string,
            jobId: number,
        ) {
            return generatePath(this.webviewDetails, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                submittalId,
                jobId,
            });
        },
        getDetailLinkForContext(
            id: string,
            jobId: number,
            webviewConfig: IWebviewRouteParams | undefined,
        ) {
            return webviewConfig
                ? this.getWebviewDetailsLink(webviewConfig, id, jobId)
                : this.getDetailsLink(id, jobId);
        },
        external: "(/builder)?(/owner)?(/subs)?/submittal/external",
        getExternalLink(shareToken: string, loginType: BTLoginTypes) {
            // CONTACT users should use the owner portal route
            const portalType: PortalType =
                loginType === BTLoginTypes.CONTACT ? PortalType.OWNER : (loginType as number);
            const path = `${routes.share.getLink(shareToken)}${
                "/".concat(PortalType[portalType]) ?? "/"
            }/submittal/external`;
            return path.toLowerCase();
        },
        print: getPathForAllPortals("/Submittal/Print"),
        buildPrintQuery(token: string, visibleColumnIds?: string[]) {
            const params = new URLSearchParams({ token });
            if (visibleColumnIds?.length) {
                params.set("columns", visibleColumnIds.join(","));
            }
            return params.toString();
        },
        getPrintLink(token: string, visibleColumnIds?: string[]) {
            const path = `/Submittal/Print?${this.buildPrintQuery(token, visibleColumnIds)}`;
            return addPathForCurrentPortal(path);
        },
        webviewPrint: getPathForWebviewPage("/Submittal/Print"),
        getWebviewPrintLink(
            webviewConfig: IWebviewRouteParams,
            token: string,
            visibleColumnIds?: string[],
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewPrint, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
            return `${path}?${this.buildPrintQuery(token, visibleColumnIds)}`;
        },
    },
    selection: {
        print: getPathForAllPortals("/Selection/Print"),
        getPrintLink(
            ids: number[],
            jobIds: number[],
            shouldPrintAll: boolean,
            sortColumn?: string,
            sortDirection?: SortDirection,
            selectedView?: SelectionListContainerTabs,
        ) {
            let direction: DirectionTypes = DirectionTypes.DEFAULT;
            if (sortDirection) {
                direction = sortDirection === "asc" ? DirectionTypes.ASC : DirectionTypes.DESC;
            }
            const idString = ids.join(",");
            let path = `/Selection/Print?ids=${idString}&jobIds=${jobIds.join(
                ",",
            )}&shouldPrintAll=${shouldPrintAll}`;
            path += `${sortColumn ? `&sortColumn=${sortColumn}` : ""}`;
            path += `${sortDirection ? `&sortDirection=${direction}` : ""}`;
            path += `${selectedView ? `&selectedView=${selectedView}` : ""}`;
            return addPathForCurrentPortal(path);
        },

        settings: "/SelectionSettings",
        getSettingsLink() {
            return this.settings;
        },

        cardList: getPathForAllPortals("/SelectionCards"),
        getCardListLink() {
            return addPathForCurrentPortal("/SelectionCards");
        },

        details: getPathForAllPortals("/Selection/:id/:jobId"),
        getDetailsLink(id: number, jobId: number, keyChoiceId?: number) {
            const path = `${generatePath(this.details, { id, jobId })}${
                keyChoiceId ? `?lockingChoiceId=${keyChoiceId}` : ""
            }`;
            return addPathForCurrentPortal(path);
        },

        quickAdd: "/SelectionQuickAdd/:jobId",
        getQuickAddLink(jobId: number) {
            return generatePath(this.quickAdd, { jobId });
        },
        webviewDetails: getPathForWebviewPage("/Selection/:id/:jobId"),
        getWebviewDetailsLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    id,
                    jobId,
                }),
            );
        },

        list: getPathForAllPortals("/Selections/:tab"),
        getListLink(
            tab: SelectionListContainerTabs = SelectionListContainerTabs.Default,
            systemFilterType?: SystemFilterType,
        ) {
            let url = addPathForCurrentPortal(
                generatePath(`/Selections/${SelectionListContainerTabs[tab]}`, { tab }),
            );
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },

        webviewList: getPathForWebviewPage("/SelectionsList/:tab?"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            tab?: SelectionListContainerTabs,
            queryParams?: { jobIds?: string },
        ) {
            const tabString =
                tab != null && tab !== SelectionListContainerTabs.Default
                    ? SelectionListContainerTabs[tab]
                    : undefined;
            const path = addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    tab: tabString,
                }),
            );

            if (!queryParams?.jobIds) {
                return path;
            }

            const params = new URLSearchParams();
            params.append("jobIds", queryParams.jobIds);
            return `${path}?${params.toString()}`;
        },

        favorites: getPathForOwnerPortal("/:jobId/SelectionFavorites/"),
        getFavoritesLink(jobId: number) {
            return generatePath(this.favorites, { jobId });
        },
        projectedTotal: getPathForOwnerPortal("/:jobId/ProjectedTotal/"),
        getProjectedTotalLink(jobId: number) {
            return generatePath(this.projectedTotal, { jobId });
        },
        subSelectionsList: "/Subs/Selections",
        getSubSelectionsListLink() {
            return this.subSelectionsList;
        },
        webviewSubList: getPathForWebviewPage("/Subs/Selections"),
        getWebviewSubListLink(
            webviewConfig: IWebviewRouteParams,
            queryParams?: { jobIds?: string },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewSubList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );

            if (!queryParams?.jobIds) {
                return path;
            }

            const params = new URLSearchParams();
            params.append("jobIds", queryParams.jobIds);
            return `${path}?${params.toString()}`;
        },
        subSelectionsSelection: "/Subs/Selections/Selection/:id/:jobId",
        getSubSelectionsSelectionLink(id: number, jobId: number) {
            return generatePath(this.subSelectionsSelection, { id, jobId });
        },
        subSelectionsConversation:
            "/Subs/Selections/Conversation/:entityId/:entityType/:builderId/:jobId?",
        getSubSelectionsConversationLink(
            entityId: number,
            entityType: number,
            builderId: number,
            jobId?: number,
        ) {
            return generatePath(this.subSelectionsConversation, {
                entityId,
                entityType,
                builderId,
                jobId,
            });
        },
    },
    selectionChoice: {
        details: getPathForAllPortals("/SelectionChoice/:choiceId/:jobId/:selectionId(\\d+)?"),
        getDetailsLink(choiceId: number, jobId: number, selectionId?: number) {
            const path = generatePath(this.details, { choiceId, jobId, selectionId });
            return addPathForCurrentPortal(path);
        },
        webviewDetails: getPathForWebviewPage(
            "/SelectionChoice/:choiceId/:jobId/:selectionId(\\d+)?",
        ),
        getWebviewDetailsLink(
            webviewConfig: IWebviewRouteParams,
            choiceId: number,
            jobId: number,
            selectionId?: number,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    choiceId,
                    jobId,
                    selectionId,
                }),
            );
        },
        dependentSelections: "/DependentSelections/:choiceId/:jobId",
        getDependentSelectionsLink(choiceId: number, jobId: number) {
            return generatePath(this.dependentSelections, { choiceId, jobId });
        },
        copyChoicesFromSelection: "/CopyChoicesFromSelection/:targetSelectionId/:jobId",
        getCopyChoicesFromSelectionLink(targetSelectionId: number, jobId: number) {
            return generatePath(this.copyChoicesFromSelection, { targetSelectionId, jobId });
        },
        signaturePad: "/:jobId/SelectionChoice/:choiceIds/SignaturePad",
        getSignaturePadLink(jobId: number, choiceIds: number[]) {
            return generatePath(this.signaturePad, { jobId, choiceIds: choiceIds.join(",") });
        },
    },
    selectionOptions: {
        list: "/Selections/Default",
        ownerList: "/Owner/Selections/Default",
        webviewList: getPathForWebviewPage("/Selections/Default"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        details: getPathForAllPortals(
            "/SelectionOption/:selectionOptionId/:jobId/:stateOverride?/:selectionOptionGroupId?",
        ),
        modalDetails: getPathForAllPortals(
            "/SelectionOptionsModal/:selectionOptionId/:jobId/:stateOverride?/:selectionOptionGroupId?",
        ),
        webviewDetails: getPathForWebviewPage(
            "/SelectionOption/:selectionOptionId/:jobId/:stateOverride?/:selectionOptionGroupId?",
        ),
        webviewDetailsModal: getPathForWebviewPage(
            "/SelectionOptionsModal/:selectionOptionId/:jobId/:stateOverride?/:selectionOptionGroupId?",
        ),
        ownerDetails: "/Owner/SelectionOption/:selectionOptionId/:jobId",
        getDetailsLink(
            selectionOptionId: number,
            jobId: number,
            stateOverride: SelectionOptionStateOverride = "View",
            selectionOptionGroupId: number = 0,
            useModal: boolean = false,
        ) {
            // client details are always in modal
            const pathToUse = useModal ? this.modalDetails : this.details;
            const path = generatePath(pathToUse, {
                selectionOptionId,
                jobId,
                stateOverride,
                selectionOptionGroupId,
            });
            return addPathForCurrentPortal(path);
        },
        getWebviewDetailsLink(
            selectionOptionId: number,
            jobId: number,
            webviewConfig: IWebviewRouteParams,
            stateOverride: SelectionOptionStateOverride = "View",
            selectionOptionGroupId: number = 0,
            useModal: boolean = false,
        ) {
            const pathToUse = useModal ? this.webviewDetailsModal : this.webviewDetails;
            const path = generatePath(pathToUse, {
                selectionOptionId,
                jobId,
                stateOverride,
                selectionOptionGroupId,
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
            });
            return addPathForCurrentPortal(path);
        },
    },
    allowance: {
        print: getPathForAllPortals("/Allowance/Print"),
        getPrintLink(id: number, jobId: number) {
            const path = `/Allowance/Print?id=${id}&jobId=${jobId}`;
            return addPathForCurrentPortal(path);
        },
        details: getPathForBuilderOrOwnerPortals("/Allowance/:id/:jobId"),
        getDetailsLink(id: number, jobId: number) {
            const path = generatePath(this.details, { id, jobId });
            return addPathForCurrentPortal(path);
        },
        getModalLink(id: number, jobId: number) {
            return generatePath(this.details, { id, jobId });
        },
        webview: getPathForWebviewPage("/Allowance/:id/:jobId"),
    },
    survey: {
        print: getPathForAllPortals("/Survey/Print"),
        getPrintLink(ids: number[]) {
            const path = `/Survey/Print?ids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },

        questionReportPrint: getPathForAllPortals("/Survey/QuestionsPrint"),
        getQuestionReportPrintLink(jobId: number) {
            const path = `/Survey/QuestionsPrint?jobId=${jobId}`;
            return addPathForCurrentPortal(path);
        },

        questionDetails: getPathForAllPortals("/SurveyQuestion/:id"),
        getQuestionDetailsLink(id: number) {
            return generatePath(this.questionDetails, { id });
        },

        surveyDefinition: "/SurveyDefinition/:id",
        getSurveyDefinitionLink(id: number) {
            return generatePath(this.surveyDefinition, { id });
        },

        settings: "/SurveySettings",
        getSettingsLink() {
            return this.settings;
        },

        questionReport: getPathForAllPortals("/QuestionReport/:id/:jobIds"),
        getQuestionReport(id: number, jobIds: number[]) {
            const jobIdsString = jobIds.join(",");
            return generatePath(this.questionReport, { id, jobIds: jobIdsString });
        },

        details: "/Survey/:id/:jobId/:surveyDefinitionId?",
        getDetailsLink(id: number, jobId: number, surveyDefinitionId?: number) {
            return generatePath(this.details, { id, jobId, surveyDefinitionId });
        },

        ownerSurvey: getPathForBuilderOrOwnerPortals("/Survey/:id"),
        getOwnerSurvey(id: number) {
            return generatePath(this.ownerSurvey, { id });
        },

        ownerList: getPathForBuilderOrOwnerPortals("/OwnerSurveys"),
        getOwnerSurveyList() {
            return addPathForCurrentPortal("/OwnerSurveys");
        },

        list: getPathForAllPortals("/Surveys/:tab"),
        getBuilderListLink(tab: SurveyListContainerTabs = SurveyListContainerTabs.Individual) {
            const tabNameFromEnum = SurveyListContainerTabs[tab];
            const path = generatePath("/Surveys/:tab", { tab: tabNameFromEnum });
            return addPathForCurrentPortal(path);
        },
        getListLink(
            loginType: BTLoginTypes,
            tab: SurveyListContainerTabs = SurveyListContainerTabs.Individual,
        ) {
            if (loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT) {
                return this.getOwnerSurveyList();
            }
            return this.getBuilderListLink(tab);
        },

        externalOwnerSurvey: "/OwnerSurvey",
        getExternalOwnerSurveyLink(shareToken: string) {
            return `${routes.share.getLink(shareToken)}${this.externalOwnerSurvey}`;
        },
    },
    tag: {
        details: "/Tag/:tagType/:tagId",
        getDetailsLink(tagType: TagTypes, tagId: number) {
            return generatePath(this.details, { tagId, tagType });
        },
    },
    chat: {
        list: getPathForAllPortals("/Chat"),
        getListLink() {
            return addPathForCurrentPortal(generatePath(this.list));
        },
        getDraftChannelPathWithParticipantLink(
            participantId: number,
            participantName: string,
        ): string {
            return (
                this.getListLink() +
                this.getDraftChannelPathWithParticipantPath(participantId, participantName)
            );
        },
        search: "/Search",
        channel: "/Channel/:channelId",
        draftChannelWithParticipant: `/Channel/:channelId/:participantId/:participantName`,
        createChannelUrlParam: "Create" as const,
        getChannelPath(channelId: string, jumpToMessageId?: string): string {
            const path = generatePath(this.channel, { channelId });
            return jumpToMessageId ? `${path}#${jumpToMessageId}` : path;
        },
        getDraftChannelPath(): string {
            return this.getChannelPath(this.createChannelUrlParam);
        },
        getChannelPathWithMesssageAsFocus(channelId: string, messageId: string) {
            return `${this.getChannelPath(channelId)}#${messageId}`;
        },
        getDraftChannelPathWithParticipantPath(
            participantId: number,
            participantName: string,
        ): string {
            return generatePath(this.draftChannelWithParticipant, {
                channelId: this.createChannelUrlParam,
                participantId,
                participantName,
            });
        },

        webview: getPathForWebviewPage("/Chat"),
        getWebviewLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
    },
    toDo: {
        list: getPathForAllPortals("/ToDos"),
        getListLink(portalTypeOverride?: PortalType) {
            if (portalTypeOverride) {
                return addPathForPortal("/ToDos", portalTypeOverride);
            }
            return addPathForCurrentPortal("/ToDos");
        },
        details: getPathForAllPortals("/ToDo/:id/:jobId/:isFromRfi"),
        getDetailsPath(id: number, jobId: number, isFromRfi: boolean) {
            return generatePath(this.details, { id, jobId, isFromRfi });
        },
        getDetailsLink(
            id: number,
            jobId: number,
            refreshParentAfterSave?: boolean,
            isFromRfi: boolean = false,
        ) {
            let path = this.getDetailsPath(id, jobId, isFromRfi);
            if (refreshParentAfterSave) {
                path += "?refreshParentAfterSave=true";
            }
            return addPathForCurrentPortal(path);
        },

        getFullDetailsLink(
            portalType: PortalType,
            id: number,
            jobId: number,
            refreshParentAfterSave?: boolean,
            isFromRfi: boolean = false,
        ) {
            return (
                this.getListLink(portalType) +
                this.getDetailsLink(id, jobId, refreshParentAfterSave, isFromRfi)
            );
        },
        // Parses a path even if it's appended to the end of another path, for example the
        // schedules page.
        parseDetailsPath(pathname: string) {
            const result = matchPath<{ id: string; jobId: string; isFromRfi: string }>(pathname, {
                path: ["**/ToDo/:id/:jobId/:isFromRfi(true|false)"],
                exact: false,
                strict: false,
            });
            if (!result) return null;
            return {
                id: parseInt(result.params.id),
                jobId: parseInt(result.params.jobId),
                isFromRfi: result.params.isFromRfi === "true",
                entityType: EntityTypes.Todo,
            };
        },

        print: getPathForAllPortals("/ToDo/Print"),
        getPrintLink(completedDetails: boolean, ids: number[]) {
            const path = `/ToDo/Print?completedDetails=${completedDetails}&toDoIds=${ids.join(
                ",",
            )}`;
            return addPathForCurrentPortal(path);
        },

        settings: "/ToDoSettings",
        getSettingsLink() {
            return this.settings;
        },

        assign: "/ToDo/Assign/:toDoIds",
        getToDoAssignLink(toDoIds: number[]) {
            const path = generatePath(this.assign, { toDoIds: toDoIds.join(",") });
            return addPathForCurrentPortal(path);
        },

        detailsCopyModal: "/ToDoCopy",
        getDetailsCopyModalLink() {
            return this.detailsCopyModal;
        },

        bulkDelete: "/ToDoBulkDelete/:builderId/:todoIds/:jobId?",
        getToDoBulkDeleteLink(todoIds: number[], builderId: number, jobId?: number) {
            const path = generatePath(this.bulkDelete, {
                builderId: builderId,
                todoIds: todoIds.join(","),
                jobId: jobId,
            });
            return addPathForCurrentPortal(path);
        },
    },
    rfi: {
        details: getPathForAllPortals("/RFI/:id/:jobId"),
        getDetailsLink(id: number, jobId: number) {
            const path = generatePath("/RFI/:id/:jobId", { id, jobId });
            return addPathForCurrentPortal(path);
        },

        related: getPathForBuilderOrSubPortals(
            "/RelatedRfi/:linkedId/:linkedType/:builderId/:jobId",
        ),
        getRelatedLink(linkedId: number, linkedType: number, builderId: number, jobId: number) {
            const path = generatePath(this.related, { linkedId, linkedType, builderId, jobId });
            return addPathForCurrentPortal(path);
        },

        print: getPathForBuilderOrSubPortals("/RFI/Print"),
        getPrintLink(id: number) {
            return addPathForCurrentPortal(`/RFI/Print?id=${id}`);
        },

        settings: "/RFISettings",
        getSettingsLink() {
            return this.settings;
        },

        bidPackageDetails: "/BidPackage/:bidPackageId",
        getBidPackageDetailsLink(bidPackageId: number) {
            return generatePath(this.bidPackageDetails, { bidPackageId });
        },

        webviewDetails: getPathForWebviewPage("/RFI/:id/:jobId"),
        webviewDetailsLinked: getPathForWebviewPage("/RFI/:id/:jobId/:linkedId/:linkedType"),
        getWebviewDetailsLink(
            id: number,
            jobId: number,
            webviewConfig: IWebviewRouteParams,
            linkedId?: number,
            linkedType?: number,
        ) {
            const platformParams = {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
            };
            if (linkedId !== undefined && linkedType !== undefined) {
                return addPathForCurrentPortal(
                    generatePath(this.webviewDetailsLinked, {
                        id,
                        jobId,
                        linkedId,
                        linkedType,
                        ...platformParams,
                    }),
                );
            }
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    id,
                    jobId,
                    ...platformParams,
                }),
            );
        },
        relatedWebview: getPathForWebviewPage(
            "/RelatedRfi/:linkedId/:linkedType/:builderId/:jobId",
        ),

        list: getPathForAllPortals("/RFIs"),
        getListLink() {
            return addPathForCurrentPortal("/RFIs");
        },

        webviewList: getPathForWebviewPage("/RFIs"),
    },
    onlinePayments: {
        settings: "/OnlinePaymentSettings",
        getSettingsLink() {
            return this.settings;
        },
        list: getPathForAllPortals("/OnlinePayments"),
        getListLink() {
            return addPathForCurrentPortal("/OnlinePayments");
        },
        paymentReportWebview: getPathForWebviewPage("/OnlinePaymentReport"),
        paymentReport: getPathForBuilderOrOwnerPortals("/OnlinePaymentReport"),
        getPaymentReportLink(loginType: BTLoginTypes) {
            if (loginType === BTLoginTypes.BUILDER) {
                return generatePath(this.paymentReport);
            } else if (loginType === BTLoginTypes.SUBS) {
                return this.paymentReportSubs;
            }
            throw new Error("Invalid login type");
        },
        paymentReportSubs: "/subs/OnlinePaymentReport",
        paymentReportSubsWebview: getPathForWebviewPage("/OnlinePaymentReport"),
        getAdminReportLink() {
            return addPathForCurrentPortal("/OnlinePayments");
        },
        externalPostOnlinePayment: "/OwnerMassOnlinePaymentExternal",
        getExternalPostPaymentLink() {
            return generatePath(this.externalPostOnlinePayment);
        },
        paymentsHostedOnboarding: "/PaymentsHostedOnboarding",
        PaymentTos: "/PaymentTos/:service",
        getPaymentTosLink(service: string) {
            const path = generatePath(this.PaymentTos, { service });
            return addPathForCurrentPortal(path);
        },
        onlinePaymentDetails: getPathForAllPortals("/OnlinePayment/:id"),
        getOnlinePaymentDetailsLink(id: number) {
            const path = generatePath("/OnlinePayment/:id", { id });
            return addPathForCurrentPortal(path);
        },
        getPaymentFromPrintoutUrl: (location: Location) => {
            return `${location.hostname}/pay`;
        },
        OnlinePaymentCodeEntry: getPathForExternalOwnerPage("pay"),
        OnlineInvoiceCodeEntry: getPathForInvoiceExternalOwnerPage("checkout/:code"),
        OnlineInvoiceCodeRefEntry: getPathForInvoiceExternalOwnerPage("checkout/:code/:referralBy"),
        OutboundPaymentCodeEntry: "/billpay/:code",
        onlinePaymentAccountDetails: getPathForAdminPortal("/OnlinePaymentAccount/:builderId/:id"),
        getOnlinePaymentAccountDetailsLink(builderId: number, id: string) {
            const path = generatePath("/OnlinePaymentAccount/:builderId/:id", { builderId, id });
            return addPathForCurrentPortal(path);
        },
        enableLending: getPathForAdminPortal("/Lending"),
        getEnableLendingLink() {
            const path = generatePath("/Lending", {});
            return addPathForCurrentPortal(path);
        },
        addLoan: getPathForAdminPortal("/Loan"),
        getAddLoanLink() {
            const path = generatePath("/Loan", {});
            return addPathForCurrentPortal(path);
        },
        disconnectLoan: getPathForAdminPortal("/DisconnectLoan"),
        getDisconnectLoanLink() {
            const path = generatePath("/DisconnectLoan", {});
            return addPathForCurrentPortal(path);
        },
        outboundPaymentSetupWizard: "/OutboundPaymentsSetup",
        outboundPayment: "/OutboundPayment",
        recordOfflinePayment: "/RecordPayment",
        paymentOverview: "/PaymentsOverview",
        getPaymentsOverviewLink() {
            return this.paymentOverview;
        },
        paymentsOverviewWebview: getPathForWebviewPage("/PaymentsOverview"),
        paymentsHostedOnboardingWebview: getPathForWebviewPage("/PaymentsHostedOnboarding"),
        paymentServicePaymentMethod: {
            newPaymentMethodPath: "/PaymentServiceNewPaymentMethod",
            verifyPaymentMethodPath:
                "/PaymentServiceVerifyPaymentMethod/:merchantPaymentMethodId/:paymentServicePaymentMethodId/:isOutboundPaymentMethod",
            getVerifyPaymentMethodLink(
                merchantPaymentMethodId: number,
                paymentServicePaymentMethodId: string,
                isOutboundPaymentMethod: boolean,
            ) {
                return generatePath(this.verifyPaymentMethodPath, {
                    merchantPaymentMethodId,
                    paymentServicePaymentMethodId,
                    isOutboundPaymentMethod,
                });
            },
            externalWithShareToken: getPathForExternalMicrodepositPageWithShareToken(
                "/PaymentServiceVerifyPaymentMethod/",
            ),
            external: getPathForExternalMicrodepositPage(
                "/PaymentServiceVerifyPaymentMethod/:merchantPaymentMethodId",
            ),
            getExternalLinkWithShareToken(shareToken: string) {
                return generatePath(this.externalWithShareToken, { shareToken });
            },
        },
        adminPrescreening: "/Admin/PaymentsPrescreening",
        bankValidationRedirect: "/onlinepayment/oauthredirect",
        stuckPaymentUtility: "/Admin/StuckPaymentUtility",
        PaymentControlsUtility: "/Admin/PaymentControlsUtility",
        sellOBP: "/SellOBP",
        financialDocumentCollection: "/DocumentCollection",
        adminMerchantPaymentList: "/Admin/MerchantPaymentList/:activeTab",
        getAdminMerchantPaymentListLink(activeTab: AdminOnlinePaymentReportTabs) {
            return generatePath(this.adminMerchantPaymentList, { activeTab });
        },
    },
    settlement: {
        settlementDetails: "/Settlement/:id",
        getSettlementDetailsLink(id: number) {
            const path = generatePath("/Settlement/:id", { id });
            return addPathForCurrentPortal(path);
        },
    },
    costCatalog: {
        settings: "/CostCatalogSettings",
        getSettingsLink(viewType: CostCatalogViewType) {
            return generatePath(`${this.settings}?viewType=${viewType}`);
        },
        addFromCatalog: "/Add/CostCatalog",
        getAddFromCostCatalogLink() {
            return this.addFromCatalog;
        },
        // Detail panel sub-path, relative to the catalog host's match.url.
        optionDetailPath: "/Option/:catalogOptionId",
        getOptionDetailLink(catalogOptionId: number) {
            return generatePath(this.optionDetailPath, { catalogOptionId });
        },
        costGroupDetailsPath: "/CostCatalog/CostGroup/:id",
        getCostGroupDetailsLink(id: number) {
            return generatePath(this.costGroupDetailsPath, { id });
        },
        costItemDetailsPath: "/CostCatalog/CostItem/:id",
        /**
         * @param id A cost item id, or 0 for new cost items.
         * @param costCodeId Used only when id = 0 to set the default
         *                   selected cost code when creating a new cost item.
         */
        getCostItemDetailsLink(id: number, costCodeId?: number | null) {
            const path = generatePath(this.costItemDetailsPath, { id });
            if (id === 0 && costCodeId) {
                return `${path}?costCodeId=${costCodeId}`;
            }
            return path;
        },
        costCodePath: "/CostCatalog/CostCode/:id",
        getCostCodeLink(id: number) {
            return generatePath(this.costCodePath, { id });
        },
    },
    costCodeManagement: {
        costCodes: "/Settings/CostCodes",
    },
    costCode: {
        list: "/CostCodes",
        settings: "/CostCodeSettings",
        getSettingsLink() {
            return this.settings;
        },
        accountingImport: "/CostCodeSettings/AccountingImport",
        getAccountingImportLink() {
            return addPathForCurrentPortal(this.accountingImport);
        },
        legacyAccountingImport: "/AccountingImport",
        getLegacyAccountingImportLink() {
            return addPathForCurrentPortal(this.legacyAccountingImport);
        },
        quickFixManageAccounting:
            "/ManageAccounting/:checkExpenseAccount/:serviceItemsOnly/:doRefresh",
        manageAccounting:
            "/CostCodeSettings/ManageAccounting/:checkExpenseAccount/:serviceItemsOnly/:doRefresh",
        getManageAccountingLink(
            costCodeIdsList: number[],
            checkExpenseAccount: boolean,
            serviceItemsOnly: boolean,
            doRefresh: boolean,
        ) {
            const path = `${generatePath(this.manageAccounting, {
                checkExpenseAccount,
                serviceItemsOnly,
                doRefresh,
            })}?costCodeIds=${costCodeIdsList.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        costCodeConflict: "/Conflict",
        getCostCodeConflictLink() {
            const path = generatePath(this.costCodeConflict);
            return addPathForCurrentPortal(path);
        },
        costCodeDetails: "/CostCode/Code/:costType/:costCodeId",
        getCostCodeDetailsLink(costCodeId: number, costType: CostType) {
            const path = generatePath(this.costCodeDetails, {
                costType: CostType[costType],
                costCodeId,
            });
            return addPathForCurrentPortal(path);
        },

        costCodeSetupWizard: "/CostCode/SetupWizard",
        getCostCodeSetupWizardLink() {
            return addPathForCurrentPortal(this.costCodeSetupWizard);
        },
        importRecommendedCostCodes: "/CostCode/ImportRecommended",
        getImportRecommendedCostCodesLink() {
            return addPathForCurrentPortal(this.importRecommendedCostCodes);
        },

        costCodeDetailsIndex: `/CostCode/Code/:costType?/:costCodeId?/:index?`,
        getCostCodeDetailsIndexLink(costCodeId: number, costType: CostType, index?: number) {
            const path = generatePath(this.costCodeDetailsIndex, {
                costCodeId,
                costType: CostType[costType],
                index,
            });
            return addPathForCurrentPortal(path);
        },

        getCostCodeDetailsIndexRoute(costType: CostType, index: number) {
            return `/CostCode/Code/${CostType[costType]}/:costCodeId/${index}`;
        },

        costCategoryDetails: "/Category/:categoryType/:costCategoryId",
        getCostCategory(costCategoryId: number, categoryType: CostType) {
            const path = generatePath(this.costCategoryDetails, {
                categoryType: CostType[categoryType],
                costCategoryId,
            });
            return addPathForCurrentPortal(path);
        },

        costCodeMassReassign: "/CostCode/CostCodeMassReassign/:costType",
        getCostCodeMassReassign(costType: CostType) {
            const path = generatePath(this.costCodeMassReassign, {
                costType: CostType[costType],
            });
            return addPathForCurrentPortal(path);
        },
        importFromExcel: "/CostCode/ImportFromExcel",
        getImportFromExcelLink() {
            return addPathForCurrentPortal(this.importFromExcel);
        },
        addCostCodes: "/CostCode/Add",
        getAddCostCodesLink() {
            return addPathForCurrentPortal(this.addCostCodes);
        },
    },
    costGroup: {
        detailsPath: "/CostGroup/:id",
        getDetailsLink(id: number) {
            return generatePath(this.detailsPath, { id });
        },
    },
    costItem: {
        detailsPath: "/CostItem/:id",
        getDetailsLink(id: number) {
            return generatePath(this.detailsPath, { id });
        },
    },
    lead: {
        details: "/Lead/:id",
        getDetailsLink(id: number) {
            return generatePath(this.details, { id });
        },

        webview: getPathForWebviewPage("/Lead/:id"),

        print: getPathForAllPortals("/Lead/Print"),
        getPrintLink(ids: number[]) {
            const path = `/Lead/Print?ids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },

        settings: "/SalesSettings",
        getSettingsLink() {
            return this.settings;
        },

        generationSettings: "/LeadGenerationSettings",
        getGenerationSettingsLink() {
            return this.generationSettings;
        },

        settingsCustomField: "/SalesSettings/CustomField/:id",
        getSettingsCustomField(id: number) {
            return `${this.settings}/CustomField/${id}`;
        },

        list: "/Leads",
        getListLink() {
            return "/Leads";
        },

        webviewList: getPathForWebviewPage("/Leads/:tab"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            tab: LeadTabTypes = LeadTabTypes.ListView,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    tab: getLeadTabUrlSegment(tab),
                }),
            );
        },

        leadList: "/leads/:tab",
        getLeadListLink(tab: LeadTabTypes = LeadTabTypes.ListView) {
            return generatePath(this.leadList, { tab: getLeadTabUrlSegment(tab) });
        },

        emailWizard: "/Leads/EmailWizard/:ids",
        getEmailWizardLink(ids: number[]) {
            return generatePath(this.emailWizard, { ids: ids.join(",") });
        },

        emailUnsubscribe: "/Leads/EmailUnsubscribe",

        getEmailUnsubscribeLink(
            builderId: string,
            emailAddress: string,
            leadId: string,
            mt: string,
            loginType: BTLoginTypes,
        ) {
            const id = `&id=${builderId}`;
            const ea = `&ea=${emailAddress}`;
            const b = `&b=${leadId}`;
            const _mt = `&mt=${mt}`;
            const loginTypeString = `&loginType=${loginType}`;
            return `${this.emailUnsubscribe}?${id}${ea}${b}${_mt}${loginTypeString}`;
        },

        emailResubscribe: "/Leads/EmailResubscribe",

        websiteContactForm: "/Leads/WebsiteContactForm",
        getWebsiteContactForm() {
            return this.websiteContactForm;
        },

        websiteContactFormInstallationInstructions: "/InstallationInstructions",
        getWebsiteContactFormInstallationInstructions() {
            return this.websiteContactFormInstallationInstructions;
        },

        webviewContactFormPreview: getPathForWebviewPage("/Leads/WebsiteContactForm/Preview"),
        getWebviewContactFormPreviewLink(webviewConfig: IWebviewRouteParams, builderToken: string) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewContactFormPreview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
            return `${path}?builderToken=${encodeURIComponent(builderToken)}`;
        },

        leadToJob: "/LeadToJob/:mode",
        getConvertLeadToJob(mode: LeadToJobModes) {
            return generatePath(this.leadToJob, { mode: LeadToJobModes[mode] });
        },

        newJobFromTemplates: "/NewJobFromTemplates",
        getNewJobFromTemplates() {
            return this.newJobFromTemplates;
        },
    },
    leadProposals: {
        details: "/LeadProposal/:proposalId/:leadId/:generatePDF",
        getDetailsLink(proposalId: number, leadId: number, generatePDF: boolean) {
            return generatePath(this.details, { proposalId, leadId, generatePDF });
        },
        list: getPathForAllPortals("/LeadProposals"),
        getListLink() {
            return addPathForCurrentPortal("/LeadProposals");
        },
        templateDetails: "/LeadProposalTemplate/:proposalTemplateId",
        getTemplateDetailsLink(proposalTemplateId: number) {
            return generatePath(this.templateDetails, { proposalTemplateId });
        },
        templateList: getPathForAllPortals("/LeadProposalTemplates"),
        getTemplateListLink() {
            return addPathForCurrentPortal("/LeadProposalTemplates");
        },
        leadProposalPrint: "/LeadProposal/Print/:proposalId",
        getLeadProposalPrintLink(proposalId: number) {
            return generatePath(this.leadProposalPrint, { proposalId });
        },
        leadProposalPrintExternal: getPathForExternalLeadsPage("/LeadProposal/Print/:proposalId"),
        getLeadProposalExternalPrintLink(builderId: string, proposalId: string) {
            return generatePath(this.leadProposalPrintExternal, { builderId, proposalId });
        },
        externalDetails: getPathForExternalLeadsPage(
            // The .{36} regex for the shareToken optional path parameter verifies that the Guid passed in has exactly 36 characters.
            // We need verification to allow the pay lead proposal modal to work when the shareToken Guid is not present
            "/LeadProposal/:leadId/:proposalId/:isBuilder/:shareToken(.{36})?",
        ),
        checkoutExternal: "/share/:shareToken/:builderId/:proposalId/:isBuilder/Proposal/checkout",
        checkoutBuilder: addPathForPortal("/LeadProposal/:proposalId/checkout", PortalType.BUILDER),
        getCheckoutBuilderPath(proposalId: number) {
            return `${routes.appBase}${generatePath(this.checkoutBuilder, {
                proposalId,
            })}`;
        },
        getCheckoutExternalPath(
            builderId: string,
            proposalId: string,
            isBuilder: boolean,
            shareToken?: string,
        ) {
            return `${routes.appBase}${generatePath(this.checkoutExternal, {
                builderId,
                proposalId,
                isBuilder,
                shareToken,
            })}`;
        },
        getExternalDetailsLink(
            builderId: string,
            leadId: string,
            proposalId: string,
            isBuilder: boolean,
            shareToken?: string,
        ) {
            return `${generatePath(this.externalDetails, {
                builderId,
                leadId,
                proposalId,
                isBuilder,
                shareToken,
            })}`;
        },
        mobilePreview: "/LeadProposalMobilePreview/:proposalId",
        mobilePreviewLegacy: "/LeadProposalMobilePreview/:leadId/:proposalId",
        proposalPdfGenerationPage: getPathForExternalLeadsPage(
            "/Proposal/ProposalPdfGeneration/:proposalId/:shareToken(.{36})?",
        ),
        payOnlineMobile: "/LeadProposal/:id/payOnlineMobile",
        getProposalPDFGenerationURL(
            builderId: string,
            proposalId: string,
            shareToken: string,
            isLeadProposal: boolean,
        ) {
            let path = generatePath(this.proposalPdfGenerationPage, {
                builderId,
                proposalId,
                shareToken,
            });

            if (isLeadProposal) {
                path += "?isLeadProposal=true";
            }
            return path;
        },

        importFromCostCodes: "/ImportFromCostCodes",
        getImportFromCostCodes() {
            return this.importFromCostCodes;
        },
    },
    leadActivity: {
        colorChange: "/LeadActivityColorChange/:activityIds",
        getColorChangeLink(activityIds: number[]) {
            return generatePath(this.colorChange, { activityIds: activityIds.join(",") });
        },

        delete: "/LeadActivityDelete/:activityIds",
        getDeleteLink(activityIds: number[]) {
            return generatePath(this.delete, { activityIds: activityIds.join(",") });
        },

        details: "/LeadActivity/:id/:leadId/:openActivity",
        getDetailsLink(
            id: number,
            leadId: number,
            openActivity: OpenActivityType,
            activityDate: moment.Moment | undefined,
            logCompletedAct: boolean = false,
        ) {
            let path = generatePath(this.details, { id, leadId, openActivity });
            if (activityDate !== undefined) {
                path = `${path}?activityDate=${activityDate.format("YYYY-MM-DD")}`;
            }
            if (logCompletedAct && openActivity === OpenActivityType.LogCompleted) {
                path = `${path}?logCompletedAct=true`;
            }
            return path;
        },

        list: "/LeadActivities",
        calendar: "/LeadActivityCalendar",
        reassign: "/LeadActivityReassign/:activityIds",
        getReassignLink(activityIds: number[]) {
            return generatePath(this.reassign, { activityIds: activityIds.join(",") });
        },

        reassignWrapper: "/LeadActivityReassign/:activityIds/:builderId",
        getReassignWrapperLink(activityIds: number[], builderId: number) {
            return generatePath(this.reassignWrapper, {
                activityIds: activityIds.join(","),
                builderId,
            });
        },

        webview: getPathForWebviewPage("/LeadActivity/:id/:leadId/:openActivity"),

        templateDetails: getPathForAllPortals("/LeadActivityTemplate/:id"),
        getTemplateDetailsLink(id: number) {
            const path = generatePath(this.templateDetails, { id });
            return addPathForCurrentPortal(path);
        },

        templateList: getPathForAllPortals("/LeadActivityTemplates"),
        getTemplateListLink() {
            return addPathForCurrentPortal("/LeadActivityTemplates");
        },
    },
    ownerInvoice: {
        print: getPathForAllPortals("/OwnerInvoice/Print"),
        printWebview: getPathForWebviewPage(
            "/OwnerInvoice/Print/:invoiceId/:isOwnerPreview/:showCustomFields/:showPaymentCode/:showInvoiceDescription?",
        ),
        webview: getPathForWebviewPage("/OwnerInvoice/:invoiceId/:jobId"),
        getPrintLink(
            invoiceId: number,
            showCustomFields?: boolean,
            isOwnerPreview?: boolean,
            showPaymentCode?: boolean,
            showInvoiceDescription?: boolean,
        ) {
            const path = `/OwnerInvoice/Print?invoiceId=${invoiceId}&showCustomFields=${showCustomFields}&showPaymentCode=${showPaymentCode}&showInvoiceDescription=${showInvoiceDescription}&isOwnerPreview=${isOwnerPreview}`;
            return addPathForCurrentPortal(path);
        },
        externalPrint: getPathForExternalOwnerPage("/OwnerInvoice/Print"),
        externalPrintShareToken: `/printExternal/:shareToken(.{36})/:showCustomFields/:showPaymentCode/:showInvoiceDescription`,
        getExternalPrintLinkShareToken(
            showCustomFields: boolean,
            shareToken: string,
            showPaymentCode?: boolean,
            showInvoiceDescription?: boolean,
        ) {
            return generatePath(this.externalPrintShareToken, {
                shareToken,
                showCustomFields,
                showPaymentCode,
                showInvoiceDescription,
            });
        },
        // Used by pubsub to render the signable AIA cover-sheet PDF for signature capture. Renders the
        // certification signature fields Nutrient anchors to; distinct from the plain external print.
        pdfGeneration: getPathForAllPortals(
            getPathForExternalPrint("/OwnerInvoice/PdfGeneration/:shareToken(.{36})?"),
        ),
        getPdfGenerationLink(builderId: string, shareToken: string) {
            return generatePath(this.pdfGeneration, { builderId, shareToken });
        },
        checkoutOwner: addPathForPortal("/OwnerInvoice/:id/:jobId/checkout", PortalType.OWNER),
        checkoutBuilder: addPathForPortal("/OwnerInvoice/:id/:jobId/checkout", PortalType.BUILDER),
        checkoutExternal: "/share/:shareToken/owner/OwnerInvoices/Shared/checkout",
        details: getPathForBuilderOrOwnerPortals(
            "/OwnerInvoice/:id/:jobId/:payOnline/:invoiceFormat(\\d+)?",
        ),
        externalDetails: getPathForExternalOwnerPage(
            "/OwnerInvoice/:shareToken(.{36})?/:id?/:jobId?",
        ),
        sharedDetails: "/OwnerInvoiceShared/:invoiceId?/:payOnline(true|false)?",
        getDetailsPath(
            invoiceId: number,
            jobId: number,
            payOnline: boolean = false,
            invoiceFormat?: InvoiceFormat,
        ) {
            return generatePath(this.details, {
                id: invoiceId,
                jobId,
                payOnline,
                invoiceFormat,
            });
        },
        getCheckoutOwnerPath(invoiceId: number, jobId: number) {
            return `${routes.appBase}${generatePath(this.checkoutOwner, {
                id: invoiceId,
                jobId,
            })}`;
        },
        getCheckoutBuilderPath(invoiceId: number, jobId: number) {
            return `${routes.appBase}${generatePath(this.checkoutBuilder, {
                id: invoiceId,
                jobId,
            })}`;
        },
        getCheckoutExternalPath(shareToken: string) {
            return `${routes.appBase}${generatePath(this.checkoutExternal, { shareToken })}`;
        },
        getDetailsLink(
            invoiceId: number,
            jobId: number,
            payOnline: boolean = false,
            invoiceFormat?: InvoiceFormat,
        ) {
            return addPathForCurrentPortal(
                this.getDetailsPath(invoiceId, jobId, payOnline, invoiceFormat),
            );
        },
        getExternalDetailsLink(
            encodedInvoiceId?: string,
            builderId?: string,
            encryptedJobId?: string,
            shareToken?: string,
        ) {
            return `${generatePath(this.externalDetails, {
                builderId,
                id: encodedInvoiceId,
                jobId: encryptedJobId,
                shareToken,
            })}`;
        },

        getSharedDetailsLink(invoiceId?: number, payOnline: boolean = false) {
            return generatePath(this.sharedDetails, {
                invoiceId,
                payOnline,
            });
        },

        settings: "/OwnerInvoiceSettings",
        getSettingsLink() {
            return this.settings;
        },
        listWebview: getPathForWebviewPage("/OwnerInvoices"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.listWebview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        list: getPathForAllPortals("/OwnerInvoices"),
        getListLink(portalType?: PortalType, systemFilterType?: SystemFilterType) {
            let url = portalType
                ? addPathForPortal("/OwnerInvoices/Shared", portalType)
                : addPathForCurrentPortal("/OwnerInvoices");
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        getListLinkForBTAdminImpersonation() {
            return "/OwnerInvoices";
        },

        getListLinkWithInvoiceSelected(
            invoiceId: number,
            jobId: number,
            payOnline: boolean = false,
            systemFilterType?: SystemFilterType,
        ) {
            let url = addPathForCurrentPortal(
                "/OwnerInvoices" + this.getDetailsPath(invoiceId, jobId, payOnline),
            );
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        payOnlineMobile: getPathForBuilderOrOwnerPortals(
            "/OwnerInvoice/:id/:jobId/payOnlineMobile",
        ),
    },
    drawSchedule: {
        drawSchedule: "/DrawSchedule/:jobId",
        getDrawSchedule(jobId: number) {
            return generatePath(this.drawSchedule, { jobId });
        },
    },
    paymentSelector: {
        path: "/payOnline",
    },
    importWizard: {
        adminPath: "/Admin/ImportWizard",
        path: "/ImportWizard/:importType",
        getLink(importType: ImportTypes) {
            return generatePath(this.path, { importType });
        },
        info: "/ImportWizard/Info/:importType",
        getInfoLink(importType: ImportTypes) {
            return generatePath(this.info, { importType });
        },
    },
    templateImport: {
        details: "/TemplateImport/:tabId/:externalJobId?",
        getDetailsLink(tabId: TabPermissions, externalJobId?: string) {
            return generatePath(this.details, { tabId, externalJobId });
        },
    },
    template: {
        templateDetails: "/Template/:jobId",
        getTemplateDetailsLink(jobId: number) {
            return generatePath(this.templateDetails, { jobId });
        },
        templateFromTemplate: "/TemplateFromTemplate/:externalJobId?",
        getTemplateFromTemplateLink(externalJobId?: string) {
            return generatePath(this.templateFromTemplate, { externalJobId });
        },
        list: getPathForAllPortals("/Templates/:tab"),
        getListLink(tab: TemplateListTabs = TemplateListTabs.MyTemplates) {
            const tabNameFromEnum = Object.values(TemplateListTabs)[tab];
            return addPathForCurrentPortal(generatePath(this.list, { tab: tabNameFromEnum }));
        },
        copyTemplateToOrg: "/Template/CopyTemplateToOrg/:templateId",
        getCopyTemplateToOrg(templateId: number) {
            return generatePath(this.copyTemplateToOrg, { templateId });
        },
    },
    internalUsers: {
        quickAdd: "/InternalUserQuickAdd",
        list: getPathForAllPortals("/InternalUsers"),
        webview: getPathForWebviewPage("/InternalUsers"),
        getListLink() {
            return "/InternalUsers";
        },
        internalUserCustomFields: "/InternalUserSettings",
        getInternalUserCustomFieldsLink() {
            return this.internalUserCustomFields;
        },
        internalUserSettings: "/RoleManagement",
        getInternalUserSettingsLink() {
            return generatePath(this.internalUserSettings);
        },
        internalUserRoleDetails: "/RoleManagement/Role/:roleId/:copyFromId",
        getInternalUserRoleDetailsLink(roleId: number, copyFromId: number = -1) {
            return generatePath(routes.settings.settings + this.internalUserRoleDetails, {
                roleId,
                copyFromId,
            });
        },
        internalUserDetails: "/InternalUser/:id/:internalUserLoadAction?",
        getInternalUserDetailsLink(
            id: number,
            initialTab?: InternalUserTabKeys,
            internalUserLoadAction?: InternalUserLoadAction,
            userPrefMode?: boolean,
        ) {
            let path = generatePath(this.internalUserDetails, { id, internalUserLoadAction });
            const qp = new URLSearchParams();
            if (initialTab) {
                qp.set("initialTab", initialTab.toString());
            }
            if (userPrefMode) {
                qp.set("upm", userPrefMode.toString());
            }
            if (qp.toString().length > 0) {
                path = `${path}?${qp.toString()}`;
            }
            return path;
        },
        myUserDetails: "/MyUserDetails",
        /**
         * Builds the app-relative generic self-service link to the current user's own
         * Internal User Details page (defaults to the Security & Login tab), in
         * user-preference mode. The caller must prepend the base route/origin to produce
         * a full, distributable URL. The id is resolved from context at render time and is
         * intentionally never included here.
         */
        getMyUserDetailsLink(initialTab: InternalUserTabKeys = InternalUserTabKeys.SecurityLogins) {
            return `${this.myUserDetails}?initialTab=${initialTab}&upm=true`;
        },
        quickActionsUserPreferences: "/QuickActionsUserPreferences",
        getQuickActionsUserPreferencesLink() {
            return generatePath(this.quickActionsUserPreferences);
        },
    },
    subscription: {
        adminList: "/Admin/Subscription/:builderId/:isAdminUser",
        getAdminListLink(builderId: number, isAdminUser: boolean) {
            return generatePath(this.adminList, { builderId, isAdminUser });
        },
    },
    subscriptionManager: {
        settings: "/SubscriptionSettings/:fromSettings/:fromFrozenBuilder",
        getSettingsLink(
            fromSettings: boolean,
            fromFrozenBuilder: boolean,
            fromBuilderSetup?: boolean,
        ) {
            const path = generatePath(this.settings, { fromSettings, fromFrozenBuilder });
            return fromBuilderSetup ? `${path}?fromBuilderSetup=true` : path;
        },
    },
    dailyLog: {
        list: getPathForAllPortals("/DailyLogs"),
        getListLink(portalTypeOverride?: PortalType) {
            if (portalTypeOverride) {
                return addPathForPortal("/DailyLogs", portalTypeOverride);
            }
            return addPathForCurrentPortal("/DailyLogs");
        },
        print: getPathForAllPortals("/DailyLog/Print"),
        getPrintLink(filters: string, pageNumber: number, pageSize: number) {
            const path = `/DailyLog/Print?filters=${filters}&pn=${pageNumber}&ps=${pageSize}`;
            return addPathForCurrentPortal(path);
        },
        details: getPathForAllPortals(dailyLogEditPath),
        getDetailsLink(id: number, jobId: number) {
            return addPathForCurrentPortal(this.getDetailsPath(id, jobId));
        },
        // Parses a path even if it's appended to the end of another path, for example the
        // schedules page.
        parseDetailsPath(pathname: string) {
            const result = matchPath<{ id: string; jobId: string }>(pathname, {
                path: ["**/DailyLog/:id/:jobId"],
                exact: true,
                strict: false,
            });
            if (!result) return null;
            return {
                id: parseInt(result.params.id),
                jobId: parseInt(result.params.jobId),
                entityType: EntityTypes.DailyLog,
            };
        },
        getDetailsPath(id: number, jobId: number) {
            return generatePath("/DailyLog/:id/:jobId", { id, jobId });
        },
        getDetailsRecursiveLink(id: number, jobId: number) {
            return generatePath(this.details, { id, jobId });
        },
        getFullDetailsLink(portalType: PortalType, id: number, jobId: number) {
            return this.getListLink(portalType) + this.getDetailsLink(id, jobId);
        },
        // Read-only Daily Log view state (`/DailyLogView/:id/:jobId/:section?`),
        // defaulting the section segment to "none". This is the route summary
        // widgets link to (vs. `getDetailsLink`, which targets the editor).
        getViewStateLink(id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(dailyLogViewPath, { id, jobId, section: "none" }),
            );
        },
        addPage: getPathForAllPortals(dailyLogAddPath),
        getAddPageLink(jobId: number) {
            return addPathForCurrentPortal(generatePath(dailyLogAddPath, { jobId }));
        },
        settings: "/DailyLogSettings",
        getSettingsLink() {
            return this.settings;
        },
        webviewList: getPathForWebviewPage("/DailyLogs"),
        webviewDetails: getPathForWebviewPage(dailyLogEditPath),
        webviewViewState: getPathForWebviewPage(dailyLogViewPath),
        webviewAddPage: getPathForWebviewPage(dailyLogAddPath),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        getWebviewViewStateLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewViewState, {
                    id,
                    jobId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        getWebviewDetailsLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    id,
                    jobId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        getWebviewAddPageLink(webviewConfig: IWebviewRouteParams, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.webviewAddPage, {
                    jobId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
    },
    contact: {
        // remove 'isFromSelector' parameter once we don't open the page from webforms
        details: "/Contact/:id/:isFromSelector",
        getDetailsLink(id: number, isFromSelector: boolean) {
            return generatePath(this.details, { id, isFromSelector });
        },
        print: "/Contact/Print",
        getPrintLink(id: number) {
            return `${this.print}?id=${id}`;
        },
        settings: "/CustomerContactSettings",
        getSettingsLink() {
            return this.settings;
        },
        contactUs: "/ContactUs",
        getContactUsLink() {
            return this.contactUs;
        },
        contactInfoDisplay: "/ContactUsDisplay",
        getContactInfoDisplayLink() {
            return this.contactInfoDisplay;
        },
        list: getPathForAllPortals("/Contacts"),
        webview: getPathForWebviewPage("/Contacts"),
        getListLink() {
            return addPathForCurrentPortal("/Contacts");
        },
        emailAndInvite: "/Contact/emailAndInvite/",
        getEmailAndInviteLink() {
            return this.emailAndInvite;
        },
        releaseAndShareModal: "/Contact/releaseAndShareModal/",
        getReleaseAndShareModalLink() {
            return this.releaseAndShareModal;
        },
        search: "/ContactSearch",
        clientDefaultJobPermissionSettings: "/ClientJobPermissionSettings/Defaults",
        getClientDefaultJobPermissionSettings() {
            return this.clientDefaultJobPermissionSettings;
        },
    },
    comment: {
        settings: "/CommentSettings",
        getSettingsLink() {
            return this.settings;
        },
        list: getPathForAllPortals("/Comments/:tab"),
        getListLink(tab: CommentListTabKeys = CommentListTabKeys.Conversation) {
            return addPathForCurrentPortal(`/Comments/${CommentListTabKeys[tab]}`);
        },
        webview: getPathForAllPortals(
            getPathForWebviewPage("/Comments/:builderId/:entityType/:entityId/:jobId?"),
        ),
        getWebviewLink(
            webviewConfig: IWebviewRouteParams,
            builderId: number,
            entityType: DiscussionsLinkedTypes,
            entityId: number,
            jobId?: number,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    builderId,
                    entityType,
                    entityId,
                    jobId,
                }),
            );
        },
        // CommentsList route needed to differentiate between the detail Webview route above.
        // To avoid breaking changes, we will keep the old route for the conversation details route
        // and this new route will be for the list page
        webviewList: getPathForWebviewPage("/CommentsList"),
    },
    conversation: {
        details: getPathForAllPortals("/Conversation/:entityId/:entityType/:builderId/:jobId?"),
        path: "/Conversation/:entityId/:entityType/:builderId/:jobId?",
        getDetailsPath(
            entityId: number,
            entityType: DiscussionsLinkedTypes,
            builderId: number,
            jobId?: number,
        ) {
            return generatePath(this.path, {
                entityId,
                entityType,
                builderId,
                jobId,
            });
        },
        getDetailsLink(
            entityId: number,
            entityType: DiscussionsLinkedTypes,
            builderId: number,
            jobId?: number,
        ) {
            return addPathForCurrentPortal(
                this.getDetailsPath(entityId, entityType, builderId, jobId),
            );
        },
    },
    media: {
        attachment: "/Attachment/:entityName/:entityId",
        getAttachmentPrefix(entityName: string, entityId: number) {
            return generatePath(this.attachment, {
                entityName,
                entityId,
            });
        },

        settings: "/MediaSettings",
        getSettingsLink() {
            return this.settings;
        },

        copy: "/Media/Copy/:jobId",
        getCopyLink(jobId: number, mediaType: MediaType, fileIds: number[], folderIds: number[]) {
            return stringifyUrl({
                url: generatePath(this.copy, { jobId }),
                query: {
                    mediaType: mediaType.toString(),
                    mediaIds: fileIds.join(","),
                    folderIds: folderIds.join(","),
                },
            });
        },

        move: "/Media/Move/:folderId/:jobId/:mediaType",
        getMoveLink(
            folderId: number,
            jobId: number,
            mediaType: MediaType,
            fileIds: number[],
            folderIds: number[],
            initialFolderId?: number,
        ) {
            return stringifyUrl({
                url: generatePath(this.move, { folderId, jobId, mediaType }),
                query: {
                    documentInstanceIds: fileIds.join(","),
                    folderIds: folderIds.join(","),
                    initialFolderId: initialFolderId?.toString(),
                },
            });
        },

        upload: "/Media/Upload/:folderId",
        getUploadLink(folderId: number) {
            return generatePath(this.upload, { folderId });
        },

        browseBTFiles: "/Media/Browse",
        getBrowseLink({ jobId, leadId }: { jobId?: number; leadId?: number }) {
            return stringifyUrl({
                url: generatePath(this.browseBTFiles),
                query: {
                    jobId: jobId?.toString(),
                    leadId: leadId?.toString(),
                },
            });
        },

        viewingPermissions: "/Media/ViewingPermissions/:folderId/:jobId/:showSubs/:showOwner",
        getViewingPermissionsLink(
            documentInstanceIds: number[],
            folderId: number,
            jobId: number,
            mediaType: MediaType,
            viewingPermissionType?: ViewingPermissionAction,
            showOwner?: boolean,
            showSubs?: boolean,
        ) {
            return stringifyUrl({
                url: generatePath(this.viewingPermissions, {
                    folderId,
                    jobId,
                    showSubs,
                    showOwner,
                }),
                query: {
                    mediaType: mediaType.toString(),
                    docIds: documentInstanceIds.join(","),
                    viewingPermissionAction: viewingPermissionType?.toString(),
                },
            });
        },
        folderDetails: "/Media/FolderDetails/:folderId/:jobId",
        getFolderDetailsLink(
            folderId: number,
            parentFolderId: number,
            jobId: number,
            mediaType: MediaType,
        ) {
            return `${generatePath(this.folderDetails, {
                folderId,
                jobId,
            })}?mediaType=${mediaType}&parentFolderId=${parentFolderId}`;
        },
        folderProperties: "/Media/FolderProperties/:folderId/:jobId",
        getFolderPropertiesLink(
            folderId: number,
            parentFolderId: number,
            jobId: number,
            mediaType: MediaType,
        ) {
            return `${generatePath(this.folderProperties, {
                folderId,
                jobId,
            })}?mediaType=${mediaType}&parentFolderId=${parentFolderId}`;
        },

        mediaDetails: "/Media/Details/:docInstanceId/:jobId/:mediaType/:folderId/:isOwnerProfile",
        getMediaDetailsLink(
            docInstanceId: number,
            jobId: number,
            mediaType: MediaType,
            folderId: number = 0,
            isOwnerProfile: boolean = false,
        ) {
            return `${generatePath(this.mediaDetails, {
                docInstanceId,
                jobId,
                mediaType,
                folderId,
                isOwnerProfile,
            })}`;
        },

        mediaProperties:
            "/Media/Properties/:docInstanceId/:jobId?/:mediaType/:folderId/:templateType/:templateDocumentInstanceId/:activeTab",
        mediaPropertiesWebview: getPathForWebviewPage(
            "/Media/Properties/:docInstanceId/:jobId/:mediaType/:folderId/:templateType/:templateDocumentInstanceId/:activeTab",
        ),
        getMediaPropertiesLink(
            docInstanceId: number,
            mediaType: MediaType,
            jobId?: number,
            folderId: number = 0,
            templateType: TemplateType = TemplateType.None,
            templateDocumentInstanceId: number = 0,
            activeTab: MediaPropertiesTab = MediaPropertiesTab.NotSet,
            uploadNewVersion?: boolean,
        ) {
            return stringifyUrl({
                url: generatePath(this.mediaProperties, {
                    docInstanceId,
                    jobId,
                    mediaType,
                    folderId,
                    templateType,
                    templateDocumentInstanceId,
                    activeTab,
                }),
                query: {
                    uploadNewVersion: uploadNewVersion?.toString(),
                },
            });
        },

        folderViewingPermissions: `/Media/FolderViewingPermissions/:jobId/:mediaType`,
        getFolderViewingPermissionsLink(folderIds: number[], jobId: number, mediaType: MediaType) {
            return stringifyUrl({
                url: generatePath(this.folderViewingPermissions, { jobId, mediaType }),
                query: {
                    folderIds: folderIds.join(","),
                },
            });
        },

        folderEmailInfo: `/Media/FolderEmailInfo/:folderId/:jobId/:mediaType`,
        getFolderEmailInfoLink(folderId: number, jobId: number, mediaType: MediaType) {
            return generatePath(this.folderEmailInfo, { folderId, jobId, mediaType });
        },

        replace:
            "/Media/Replace/:documentInstanceId/:isFromMediaPage/:mediaType/:includeThisDoc?/:linkedByAnnotationGroup?",
        getReplaceLink(
            documentInstanceId: number,
            isFromMediaPage: boolean,
            mediaType: MediaType,
            includeThisDoc?: boolean,
            linkedByAnnotationGroup?: boolean,
        ) {
            return generatePath(this.replace, {
                documentInstanceId,
                isFromMediaPage,
                mediaType,
                includeThisDoc,
                linkedByAnnotationGroup,
            });
        },
        shared: "/Media/Shared/:shareToken(.{36})",
        getSharedLink(builderId: string, shareToken?: string) {
            return generatePath(this.shared, { builderId, shareToken });
        },
    },
    photo: {
        preview: getPathForAllPortals("/Photo/Preview/:id/:jobId"),
        getPhotoPreviewLink(documentInstanceId: number, jobId: number) {
            return generatePath(this.preview, { id: documentInstanceId, jobId });
        },
        list: getPathForAllPortals("/Photos/:directoryType/:folderId"),
        webview: getPathForWebviewPage("/Photos/:directoryType/:folderId"),
        getAdminSetupListLink() {
            return `/Photos/${DirectoryType.GlobalDocs}/0/`;
        },
        getListLink(
            directoryType: DirectoryType = DirectoryType.Standard,
            folderId: number = 0,
            selectedJobPickerId?: number,
        ) {
            let path = addPathForCurrentPortal(
                generatePath(`/Photos/${DirectoryType[directoryType]}/:folderId`, {
                    directoryType,
                    folderId,
                }),
            );
            if (selectedJobPickerId) {
                path = routes.jobPicker.selectJobLink(selectedJobPickerId, path);
            }
            return path;
        },
        draw: getPathForAllPortals(
            "/Photos/:documentInstanceId/:builderId/:loginType/:isOnNewEntity/:isOnTempFile",
        ),
        getPhotoDrawLink(
            documentInstanceId: number,
            builderId: number,
            loginType: BTLoginTypes,
            isOnNewEntity: boolean,
            isOnTempFile: boolean,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.draw, {
                    documentInstanceId,
                    builderId,
                    loginType,
                    isOnNewEntity,
                    isOnTempFile,
                }),
            );
        },
    },
    video: {
        list: getPathForAllPortals("/Videos/:directoryType/:folderId"),
        webview: getPathForWebviewPage("/Videos/:directoryType/:folderId"),
        getListLink(
            directoryType: DirectoryType = DirectoryType.Standard,
            folderId: number = 0,
            selectedJobPickerId?: number,
        ) {
            let path = addPathForCurrentPortal(
                generatePath(`/Videos/${DirectoryType[directoryType]}/:folderId`, {
                    directoryType,
                    folderId,
                }),
            );
            if (selectedJobPickerId) {
                path = routes.jobPicker.selectJobLink(selectedJobPickerId, path);
            }
            return path;
        },
        getAdminSetupListLink() {
            const path = `/Videos/${DirectoryType.GlobalDocs}/0/`;
            return routes.jobPicker.selectJobLink(JobIdTypes.GlobalDocs, path);
        },
    },
    plans: {
        list: getPathForAllPortals("/Plans/:setId([^/]{36})?"),
        getPlansListLink(setId?: string, jobId?: number) {
            const basePath = setId ? `/Plans/${setId}` : "/Plans";
            const url = addPathForCurrentPortal(basePath);
            if (jobId) {
                return routes.jobPicker.selectJobLink(jobId, url);
            }
            return url;
        },
        externalPlansList: "/Plans/External/:setId?",
        getExternalLink(shareToken: string, setId?: string) {
            const path = setId ? `/Plans/External/${setId}` : "/Plans/External";
            return `${routes.share.getLink(shareToken)}${path}`;
        },
        externalPlanPageDetails: "/Plans/Viewer/External/:pageId",
        getExternalViewerLink(shareToken: string, pageId: string) {
            return `${routes.share.getLink(shareToken)}${generatePath(
                this.externalPlanPageDetails,
                {
                    pageId,
                },
            )}`;
        },
        getExternalViewerLinkRelative(pageId: string) {
            return generatePath(this.externalPlanPageDetails, {
                pageId,
            });
        },
        viewer: getPathForAllPortals("/Plans/Viewer/:pageId"),
        getViewerLink(
            pageId: string,
            jobId?: string,
            isLinkedPlan?: boolean,
            linkedBidId?: string,
        ) {
            let path = generatePath(addPathForCurrentPortal(this.viewer), {
                pageId,
            });
            if (jobId) {
                path = addQueryParams(path, { jobId });
            }
            if (isLinkedPlan) {
                path = addQueryParams(path, { linkedPlan: "true" });
            }
            if (linkedBidId) {
                path = addQueryParams(path, { bidId: linkedBidId });
            }
            return path;
        },
        webview: getPathForWebviewPage("/Plans/:setId(.{36})?"),
        viewerWebview: getPathForWebviewPage("/Plans/Viewer/:pageId"),
        revisionsReview: getPathForAllPortals("/Plans/Revisions/:draftId"),
        revisionsReviewWebview: getPathForWebviewPage("/Plans/Revisions/:draftId"),
        getRevisionsReviewLink(draftId: string, jobId?: number) {
            let path = generatePath(addPathForCurrentPortal(this.revisionsReview), { draftId });
            if (jobId) {
                path = addQueryParams(path, { jobId: jobId.toString() });
            }
            return path;
        },
    },
    estimate: {
        list: "/Estimate",
        getListLink(jobId?: number) {
            const url = addPathForCurrentPortal("/Estimate");
            if (jobId) {
                return routes.jobPicker.selectJobLink(jobId, url);
            }
            return url;
        },

        settings: "/EstimateSettings",
        getSettingsLink() {
            return this.settings;
        },
        lineItemDetail: getPathForAllPortals("/Estimate/:jobId/:lineItemId/:lineItemType"),
        getLineItemDetail(
            lineItemId: number,
            jobId: number,
            lineItemType: LineItemType,
            parentId?: number,
        ) {
            if (parentId === undefined) {
                return generatePath(this.lineItemDetail, {
                    lineItemId,
                    jobId,
                    lineItemType,
                });
            } else {
                return `${generatePath(this.lineItemDetail, {
                    lineItemId,
                    jobId,
                    lineItemType,
                })}?parentId=${parentId}`;
            }
        },
        newAllowance: "/Allowance/New/:jobId",
        getNewAllowanceLink(jobId: number) {
            return generatePath(this.newAllowance, { jobId });
        },

        // relative path for building proposal from estimate worksheet
        buildProposal: "/Estimate/:worksheetId/BuildProposal",
        getBuildProposal(worksheetId: number) {
            return generatePath(this.buildProposal, { worksheetId });
        },

        // Path for proposal snapshot
        proposalSnapshot: "/Estimate/:worksheetId/Proposal/:proposalId",
        getProposalSnapshotLink(worksheetId: number, proposalId: number) {
            return generatePath(this.proposalSnapshot, { worksheetId, proposalId });
        },

        legacyProposal: "/Proposal/:worksheetId/:jobId",
        getProposalLink(jobId: number, worksheetId: number) {
            return generatePath(this.legacyProposal, { worksheetId, jobId });
        },

        externalProposalPage: "/share/:shareToken(.{36})/Proposal",

        worksheetPrint: "/Estimate/:worksheetId/Print",
        getWorksheetPrintLink(worksheetId: number) {
            return generatePath(this.worksheetPrint, { worksheetId });
        },

        proposalPrint: getPathForBuilderOrOwnerPortals(
            "/Estimate/:worksheetId/Proposals/:proposalId/Print",
        ),
        proposalPrintByProposalId: getPathForBuilderOrOwnerPortals("/Proposals/:proposalId/Print"),
        getProposalPrintLink(worksheetId: number, proposalId?: number) {
            let path: string;
            if (proposalId) {
                path = generatePath(this.proposalPrint, { worksheetId, proposalId });
            } else {
                path = generatePath(this.legacyProposalPrint, { worksheetId });
            }

            return addPathForCurrentPortal(path);
        },
        getProposalPrintLinkByProposalId(proposalId: number) {
            const path = generatePath(this.proposalPrintByProposalId, { proposalId });
            return addPathForCurrentPortal(path);
        },
        LegacyGetOwnerProposalLink() {
            return addPathForCurrentPortal("/Proposal");
        },
        ownerEstimate: "/Owner/Estimate",
        ownerProposal: "/Owner/Estimate/:worksheetId/Proposals/:proposalId",
        ownerProposalWebview: getPathForWebviewPage("/Estimate/:worksheetId/Proposals/:proposalId"),
        ownerProposalReview: "/Owner/ProposalReview/:proposalId",

        // path to view a proposal for a worksheet and job
        getBuilderProposalLink(worksheetId: number, proposalId: number, jobId?: number) {
            const url = generatePath(this.proposalSnapshot, {
                worksheetId,
                proposalId,
            });
            if (jobId) {
                return routes.jobPicker.selectJobLink(jobId, url);
            }
            return url;
        },

        legacyOwnerProposal: "/Owner/Proposal",
        getOwnerProposalMainNavLink() {
            return this.ownerEstimate;
        },
        getOwnerProposalLink(worksheetId: number, proposalId?: number, jobId?: number) {
            let path: string;
            if (proposalId) {
                path = generatePath(this.ownerProposal, { worksheetId, proposalId });
                if (jobId) {
                    return routes.jobPicker.selectJobLink(jobId, path);
                }
            } else {
                path = this.legacyOwnerProposal;
            }

            return path;
        },
        getOwnerProposalReviewLink(proposalId: number) {
            return generatePath(this.ownerProposalReview, { proposalId });
        },

        legacyProposalPrint: getPathForBuilderOrOwnerPortals("/Proposal/Print/:worksheetId"),

        getExternalProposalPageLink(shareToken: string) {
            return generatePath(this.externalProposalPage, {
                shareToken,
            });
        },
        proposalPrintExternal: "/share/:shareToken(.{36})/Proposal/Print",
        getExternalProposalPrintLink(shareToken: string) {
            return generatePath(this.proposalPrintExternal, { shareToken: shareToken });
        },
        getProposalPDFGenerationURL(shareToken: string) {
            return generatePath(this.proposalPdfGenerationPage, { shareToken });
        },
        proposalPdfGenerationPage: "/share/:shareToken(.{36})/Proposal/ProposalPdfGeneration",
        estimateWebview: getPathForWebviewPage("/Estimate"),
        buildProposalWebView: getPathForWebviewPage("/Estimate/:worksheetId/BuildProposal"),
        fullpageProposalWebview: getPathForWebviewPage(
            "/Estimate/:worksheetId/Proposal/:proposalId",
        ),
        addGroup: "/Group/:groupId",
        getAddGroup(groupId?: number) {
            return generatePath(this.addGroup, { groupId });
        },
    },
    proposal: {
        proposalDashboard: "/Estimate/Proposals",
        getProposalDashboardLink() {
            return generatePath(this.proposalDashboard);
        },

        // Path for past estimate worksheet
        pastWorksheet: "/Proposals/:proposalId/Worksheet",
        getPastWorksheetLink(proposalId: number) {
            return generatePath(this.pastWorksheet, { proposalId });
        },

        proposalDashboardWebview: getPathForWebviewPage("/Estimate/Proposals"),
        pastWorksheetWebview: getPathForWebviewPage("/Proposals/:proposalId/Worksheet"),
    },
    rebates: {
        settings: "/CashbackAndDiscountsSettings",
        getSettingsLink() {
            return this.settings;
        },

        details: getPathForAllPortals("/Rebate/:id"),
        getDetailsPath(rebateId: number) {
            return generatePath("/Rebate/:id", {
                id: rebateId,
            });
        },
        getDetailsLink(rebateId: number) {
            return addPathForCurrentPortal(this.getDetailsPath(rebateId));
        },

        supplier: "/Supplier/:supplierId",
        getSupplierPath(supplierId: number) {
            return generatePath("/Supplier/:supplierId", { supplierId });
        },
        getSupplierLink(supplierId: number) {
            return addPathForCurrentPortal(this.getSupplierPath(supplierId));
        },

        proofOfPurchaseDetails: getPathForAllPortals("/ProofOfPurchase/:id"),
        getProofOfPurchaseDetailsPath(proofOfPurchaseId: number) {
            return generatePath("/ProofOfPurchase/:id", { id: proofOfPurchaseId });
        },
        getProofOfPurchaseDetailsLink(proofOfPurchaseId: number) {
            return addPathForCurrentPortal(this.getProofOfPurchaseDetailsPath(proofOfPurchaseId));
        },
    },
    purchasing: {
        dashboard: "/Purchasing/:tabId",
        getDashboardLink(tab: string = PurchasingDashboardPages.Activity) {
            return addPathForCurrentPortal(generatePath("/Purchasing/:tab", { tab }));
        },
        getAllReceiptsLink() {
            return addPathForCurrentPortal(
                generatePath(
                    `/Purchasing/${PurchasingDashboardPages.Activity}/${PurchasingDashboardActivityPages.Receipts}`,
                ),
            );
        },
        getAllCashbackLink() {
            return `${this.getDashboardLink(PurchasingDashboardPages.Activity)}/${
                PurchasingDashboardActivityPages.Cashback
            }`;
        },
        enrollmentWizard: "/PurchasingEnrollment",
    },
    receipts: {
        details: getPathForAllPortals("/Receipt/:id"),
        getDetailsLink(id: number) {
            const path = this.getDetailsPath(id);
            return addPathForCurrentPortal(path);
        },
        getDetailsPath(id: number) {
            return generatePath(this.details, { id });
        },
        list: getPathForAllPortals("/Receipts"),
        getListLink() {
            return addPathForCurrentPortal("/Receipts");
        },
        webview: getPathForWebviewPage("/Receipts"),
        upload: getPathForAllPortals("/Receipts/Upload"),
        getUploadLink() {
            return addPathForCurrentPortal("/Receipts/Upload");
        },
        receiptWebview: getPathForWebviewPage("/Receipt/:receiptId"),
    },
    lienWaiver: {
        details: getPathForAllPortals("/LienWaiver/:id/:jobId"),
        getDetailsPath(id: number, jobId: number) {
            return `/LienWaiver/${id}/${jobId}`;
        },
        getDetailsLink(id: number, jobId: number) {
            return addPathForCurrentPortal(this.getDetailsPath(id, jobId));
        },
        detailsExternal: "/LienWaiverExternal/:encodedLienWaiverId/:encodedSubId",
        getDetailsPathExternal(encodedLienWaiverId: string, encodedSubId: string) {
            return generatePath(this.detailsExternal, {
                encodedLienWaiverId,
                encodedSubId,
            });
        },
        // The detail is nested under whichever review page mounted it, so the match needs that page's
        // route. Callers must share this one matcher: the review page and its list body each decide
        // what to render from the answer, and they blank the screen or stack on each other if they
        // disagree.
        matchDetailsPathExternal(pathname: string, parentRoute: string) {
            return matchPath<{ encodedLienWaiverId: string; encodedSubId: string }>(pathname, {
                path: `${parentRoute}${this.detailsExternal}`,
            });
        },
        print: getPathForAllPortals("/LienWaiver/Print"),
        getPrintLink(ids: number[]) {
            const path = `/LienWaiver/Print?popids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        getPrintExternalLink(builderId: string, shareToken: string, isPDFMode: boolean) {
            let path = generatePath(this.printExternal, { builderId, shareToken });
            path = `${path}?isPDFMode=${isPDFMode}`;
            return path;
        },
        printExternal: getPathForAllPortals(
            getPathForExternalPrint("/LienWaiver/Print/:shareToken(.{36})?"),
        ),
    },
    customFields: {
        details: "/CustomFields/:associatedType/:customFieldId",
        getDetailsLink(associatedType: number, customFieldId: number) {
            return generatePath(this.details, { associatedType, customFieldId });
        },
    },
    creditMemos: {
        print: getPathForAllPortals("/CreditMemos/Print/:id/:jobId"),
        details: getPathForAllPortals("/CreditMemos/:id/:jobId"),
        listWebview: getPathForWebviewPage("/CreditMemos"),
        printWebview: getPathForWebviewPage("/CreditMemos/Print/:id/:jobId"),
        getDetailsLink(id: number, jobId: number) {
            const path = generatePath("/CreditMemos/:id/:jobId", { id, jobId });
            return addPathForCurrentPortal(path);
        },
        getPrintLink(id: number, jobId: number) {
            const path = generatePath("/CreditMemos/Print/:id/:jobId", { id, jobId });
            return addPathForCurrentPortal(path);
        },
        list: getPathForAllPortals("/CreditMemos"),
        getListLink() {
            return addPathForCurrentPortal("/CreditMemos");
        },
        applyToInvoice: "/ApplyToInvoice/:jobId/:id",
        getApplyToInvoicePath(jobId: number, id: number) {
            return generatePath(this.applyToInvoice, { jobId, id });
        },
        webview: getPathForWebviewPage("/CreditMemos/:creditMemoId(\\d+)/:jobId"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.listWebview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
    },
    invoicePayments: {
        list: getPathForAllPortals("/InvoicePayments"),
        getListLink() {
            return addPathForCurrentPortal("/InvoicePayments");
        },
        webview: getPathForWebviewPage("/InvoicePayments"),
        getWebviewListLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
    },
    marketplace: {
        integrationSettings: "/IntegrationsSettings/:type",
        getintegrationSettingsLink(type: number) {
            return generatePath(this.integrationSettings, { type });
        },

        getMarketplaceLink: () => {
            return "https://buildertrend.com/marketplace/";
        },
        getIntegrationLink(integration: ExternalIntegrationTypes) {
            return `https://buildertrend.com/integration/${integration}`;
        },
        getPaymentsLink() {
            return `https://buildertrend.com/payments-for-customers/`;
        },
        getRedirectPath: () => addPathForCurrentPortal(marketplaceInternal.redirect),
        reconsent: "/Reconsent",
        getReconsentLink: () => {
            addPathForCurrentPortal("/Reconsent");
        },
    },
    additionalServices: {
        getAdditionalServicesLink() {
            return "https://buildertrend.com/additional-customer-services/";
        },
    },
    category: {
        details: "/category/:catId/:categoryType/:allowDeleteExistingRefs?",
        /**
         * This is not generating a link - this is generating a route with an explicit category type
         * to allow multiple category type modals to exist on the same page without interfering with
         * each other.
         */
        getDetailsRoute: (categoryType: CategoryType) => {
            return `/category/:catId/${categoryType}/:allowDeleteExistingRefs?`;
        },
        getCategoryLink(catId: number, categoryType: CategoryType) {
            return generatePath(this.details, { catId, categoryType });
        },
    },
    multiFactorAuth: {
        multiFactorAuth: "/MultiFactorAuth/:credentialId",
        getMultiFactorAuthLink(credentialId: number) {
            return generatePath(this.multiFactorAuth, { credentialId });
        },
    },
    settings: {
        settings: "/Settings",
        getSettingsLink() {
            return this.settings;
        },
    },
    sub: {
        paymentRequest: getPathForAllPortals("/PaymentRequest/:poId/:popId"),
        getPaymentRequestLink(poId: number, popId: number) {
            return generatePath(this.paymentRequest, { poId, popId });
        },
        settings: "/SubSettings",
        getSettingsLink() {
            return this.settings;
        },
        division: "/SubContractor/Division",
        getDivisionLink(ids: number[]) {
            const path = `/SubContractor/Division?subids=${ids.join(",")}`;
            return addPathForCurrentPortal(path);
        },
        details: "/SubVendor/:id/:defaultTab",
        getDetailsLink(id: number, defaultTab: SubTabKeys = SubTabKeys.AdditionalInfo) {
            return generatePath(this.details, { id, defaultTab });
        },
        setup: "/subs/Setup/:id/:initialTab/:openTradeAgreement",
        setupWebview: getPathForWebviewPage("/subs/Setup/:id/:initialTab/:openTradeAgreement"),
        getSetupLink(id: number, initialTab?: SubSetupTabKeys, openTradeAgreement?: boolean) {
            const openTrade = openTradeAgreement ?? false;
            const initTab = initialTab ?? SubSetupTabKeys.CompanyInfo;
            const path = generatePath(this.setup, {
                id,
                initialTab: initTab,
                openTradeAgreement: openTrade,
            });
            return path;
        },
        list: getPathForAllPortals("/Sub"),
        webview: getPathForWebviewPage("/Sub"),
        getListLink() {
            return addPathForCurrentPortal("/Sub");
        },
    },
    owner: {
        setup: "/Owner/Setup/:jobId",
        setupWebview: getPathForWebviewPage("/Owner/Setup/:jobId"),
        getSetupLink(jobId: number) {
            const path = generatePath(this.setup, { jobId });
            return path;
        },
        externalIdPaymentMethodApprove: "/Owner/Setup/:jobId/:externalId",
    },
    schedule: {
        detailsWebview: getPathForWebviewPage("/Schedule/:id/:jobId"),
        phaseDetails: getPathForAllPortals("/PhaseDetails/:id"),
        getPhaseDetailsLink(id: number) {
            return generatePath(this.phaseDetails, { id });
        },
        settings: "/ScheduleSettings",
        getSettingsLink() {
            return this.settings;
        },
        history: "/ScheduleHistory/:jobId",
        getHistoryLink(jobId: number) {
            return generatePath(this.history, { jobId });
        },
        details: getPathForAllPortals("/Schedule/:id/:jobId"),
        getDetailsLink(
            id: number,
            jobId: number,
            initialTab?: ScheduleTabNames,
            linkedPredId?: number,
            startDate?: moment.Moment,
            phaseId?: number,
            endDate?: moment.Moment,
            draftMode?: boolean,
        ) {
            let path = generatePath(this.details, { id, jobId });

            const qp = new URLSearchParams();
            if (initialTab) {
                qp.set("initialTab", initialTab.toString());
            }
            if (linkedPredId) {
                qp.set("linkedPredId", linkedPredId.toString());
            }
            if (startDate) {
                qp.set("startDate", startDate.toString());
            }
            if (endDate) {
                qp.set("endDate", endDate.toString());
            }
            if (phaseId) {
                qp.set("phaseId", phaseId.toString());
            }
            if (draftMode) {
                qp.set("draftMode", draftMode.toString());
            }

            if (qp.toString().length > 0) {
                path = `${path}?${qp.toString()}`;
            }

            return addPathForCurrentPortal(path);
        },
        getFullDetailsLink(params: {
            tab: ScheduleListContainerTabs;
            id: number;
            jobId: number;
            initialTab?: ScheduleTabNames;
            linkedPredId?: number;
            startDate?: moment.Moment;
            phaseId?: number;
            endDate?: moment.Moment;
            draftMode?: boolean;
        }) {
            return (
                this.getListLink(params.tab) +
                this.getDetailsLink(
                    params.id,
                    params.jobId,
                    params.initialTab,
                    params.linkedPredId,
                    params.startDate,
                    params.phaseId,
                    params.endDate,
                    params.draftMode,
                )
            );
        },
        getDefaultParamDetailsLink(
            id: number,
            jobId?: number,
            defaultParams?: ScheduleEntityDefaultParams,
        ) {
            let path = generatePath(this.details, { id, jobId });
            if (defaultParams) {
                const { title, assignedUserIds, startDate, endDate, isAllDay } = defaultParams;
                let displayColor = defaultParams.displayColor;

                if (startDate?.isValid && endDate?.isValid) {
                    // Can't pass the '#' character to a URL. Need to covert it using URL encoding.
                    if (displayColor !== null) {
                        displayColor = encodeURIComponent(`${displayColor}`);
                    } else {
                        displayColor = "";
                    }
                    path += `?title=${title}&displayColor=${displayColor}&assignedUserIds=${assignedUserIds}&startDate=${encodeURIComponent(
                        startDate.toISOString(),
                    )}&endDate=${encodeURIComponent(endDate.toISOString())}&isAllDay=${isAllDay}`;
                }
            }
            return addPathForCurrentPortal(path);
        },
        // Parses a path even if it's appended to the end of another path.
        parseDetailsPath(pathname: string) {
            const result = matchPath<{ tab: string; id: string; jobId: string }>(pathname, {
                path: ["**/Schedule/:id/:jobId"],
                exact: false,
                strict: false,
            });
            if (!result) return null;
            return {
                id: parseInt(result.params.id),
                jobId: parseInt(result.params.jobId),
                entityType: EntityTypes.Schedule,
            };
        },
        rfiDetails: getPathForAllPortals("/ScheduleRFI/:id/:jobId"),
        getRfiDetailsLink(id: number, jobId: number) {
            const path = generatePath("/ScheduleRFI/:id/:jobId", { id, jobId });
            return addPathForCurrentPortal(path);
        },
        copyDetails: "/ScheduleCopy/:id/:jobId",
        getCopyDetailsLink(id: number, jobId: number) {
            return generatePath(this.copyDetails, { id, jobId });
        },
        conflictReport: "/ScheduleConflictReport/:view/:id?",
        getConflictReportLink(view: ScheduleConflictViews, id?: number) {
            return generatePath(this.conflictReport, { view, id });
        },
        notificationPrompt: "/ScheduleNotificationPromot/:scheduleIds",
        getNotificationPromptLink(scheduleIds: number[]) {
            return generatePath(this.notificationPrompt, { scheduleIds: scheduleIds.join(",") });
        },
        baselineList: getPathForAllPortals("/Schedules/8"),
        list: getPathForAllPortals("/Schedules/:tab"),
        getListLink(tab: ScheduleListContainerTabs) {
            return addPathForCurrentPortal(generatePath("/Schedules/:tab", { tab: tab }));
        },
        workdayExceptionDetails: "/ScheduleWorkdayExceptions/:id/:jobId",
        getWorkdayDetailsLink(id: number, jobId: number) {
            return generatePath(this.workdayExceptionDetails, { id, jobId });
        },
        confirmationExternal: getPathForExternalScheduleConfirmationPage(
            "/Schedules/External/:userId/:shareToken",
        ),
        getConfirmationExternalLink(builderId: string, userId: string, shareToken: string) {
            return generatePath(this.confirmationExternal, { builderId, userId, shareToken });
        },

        baselinePage: getPathForAllPortals("/Schedules/Baseline"),
        getBaselineLink() {
            return addPathForCurrentPortal(generatePath("/Schedules/Baseline"));
        },
        calendarPage: getPathForAllPortals("/Schedules/Calendar"),
        getCalendarPageLink() {
            return addPathForCurrentPortal(generatePath("/Schedules/Calendar"));
        },
        ganttPage: getPathForAllPortals("/Schedules/Gantt"),
        getGanttPageLink() {
            return addPathForCurrentPortal(generatePath("/Schedules/Gantt"));
        },
        listPage: getPathForAllPortals("/Schedules/List"),
        getListPageLink() {
            return addPathForCurrentPortal(generatePath("/Schedules/List"));
        },
        workdayExceptionsPage: "/Schedules/WorkdayExceptions",
        getWorkdayExceptionsLink() {
            return generatePath("/Schedules/WorkdayExceptions");
        },
    },
    timeClock: {
        settings: "/TimeClockSettings",
        getSettingsLink() {
            return this.settings;
        },
        /**
         * jobId stays optional in the *matching* pattern so legacy inbound links keep resolving --
         * notification emails, bookmarks, and server-built urls (BT_WebApp ReactDialogHandler) can
         * still arrive as /Shift/42. New links must go through getShiftDetailsLink, which always
         * emits the segment.
         */
        shiftDetails: "/Shift/:id/:jobId?",
        shiftDetailsWebView: getPathForWebviewPage("/Shift/:id/:jobId?"),
        /**
         * jobId is required: generatePath drops a trailing optional param when it's undefined, and
         * the resulting 2-segment /Shift/:id collides with the appended sub-routes (eg /Tag/:tagType
         * /:tagId), which then bind "Tag" to :jobId instead of matching.
         *
         * Shifts with no real jobsite pass JobIdTypes.GlobalJob, not 0. The backend reads this
         * segment two ways and 0 is wrong for both: TimeClockLegacyService.GetJobIdDefaultForSummary
         * special-cases GlobalJob to reach the general job (0 falls through to whatever jobsite is in
         * session), and in a webview the segment feeds the job-picker session sync, where 0 means
         * AllJobs rather than "no job".
         */
        getShiftDetailsLink(id: number, jobId: number) {
            return generatePath(this.shiftDetails, {
                id,
                jobId,
            });
        },
        /**
         * The standalone shift details webview page. Unlike getShiftDetailsLink, this is an
         * absolute path, so pushing it replaces the list rather than layering the shift over it
         * -- which is what the native app expects once it has framed the shift as its own page.
         *
         * Same jobId contract as getShiftDetailsLink: always emit the segment, and send
         * JobIdTypes.GlobalJob rather than 0 for a shift with no real jobsite.
         */
        getShiftDetailsWebviewLink(webviewConfig: IWebviewRouteParams, id: number, jobId: number) {
            return addPathForCurrentPortal(
                generatePath(this.shiftDetailsWebView, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    id,
                    jobId,
                }),
            );
        },
        clockInClockOut: "/ClockInClockOut/:id/:jobId/:isClockIn/:isMultiple",
        getClockInClockOutLink(id: number, jobId: number, isClockIn: boolean, isMultiple: boolean) {
            const path = generatePath(this.clockInClockOut, {
                id,
                jobId,
                isClockIn,
                isMultiple,
            });
            return addPathForCurrentPortal(path);
        },
        list: getPathForAllPortals("/TimeClock/:tab"),

        getListLink(tab: ShiftListContainerTabs = ShiftListContainerTabs.Reports): string {
            return addPathForCurrentPortal(
                generatePath(this.list, { tab: ShiftListContainerTabs[tab] }),
            );
        },

        webview: getPathForWebviewPage("/TimeClock/:tab"),
        getWebviewLink(webviewConfig: IWebviewRouteParams, tab: ShiftListContainerTabs) {
            return addPathForCurrentPortal(
                generatePath(this.webview, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    tab: ShiftListContainerTabs[tab],
                }),
            );
        },
        clockInClockOutWebview: getPathForWebviewPage(
            "/ClockInClockOut/:id/:jobId/:isClockIn/:isMultiple",
        ),
    },
    notifications: {
        details: "/NotificationDetails/:notificationId/:jobsiteId/:dateSent/:isArchive",
        getDetailsLink(
            notificationId: number,
            jobsiteId: number,
            dateSent: moment.Moment,
            isArchive: boolean,
        ) {
            return generatePath(this.details, {
                notificationId,
                jobsiteId,
                dateSent: dateSent.utc().format(),
                isArchive,
            });
        },
        list: getPathForAllPortals("/Notifications"),
        getListLink() {
            return addPathForCurrentPortal("/Notifications");
        },
        legacyList: getPathForAllPortals("/NotificationHistory"),
        getLegacyListLink() {
            return addPathForCurrentPortal("/NotificationHistory");
        },
        webviewHistory: getPathForWebviewPage("/NotificationHistory"),
        getUserPreferencesLink: (userId: number, loginType: BTLoginTypes) => {
            if (loginType === BTLoginTypes.BUILDER) {
                return routes.internalUsers.getInternalUserDetailsLink(
                    userId,
                    InternalUserTabKeys.Notifications,
                );
            } else if (loginType === BTLoginTypes.SUBS) {
                return routes.sub.getSetupLink(userId, SubSetupTabKeys.Notifications);
            } else if (loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT) {
                return routes.owner.getSetupLink(userId);
            }
            throw new Error("not implemented");
        },
        adminNotificationHistory: "/Admin/BuilderNotificationHistory/:builderId",
        webview: getPathForWebviewPage("/Notifications"),
    },
    messages: {
        oldList: getPathForAllPortals("/Messages"),
        list: getPathForAllPortals("/Messages/:folder"),
        getListLink(
            folderId: EmailFolder | number = EmailFolder.Inbox,
            systemFilterType?: SystemFilterType,
        ) {
            let folder: string | number = folderId;
            if (folderId < 0) {
                folder = EmailFolder[folder];
            }

            const path = generatePath(this.list, { folder });
            let url = addPathForCurrentPortal(path);
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        webviewList: getPathForWebviewPage("/Messages/:folder"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            folderId: EmailFolder | number = EmailFolder.Inbox,
            systemFilterType?: SystemFilterType,
        ) {
            let folder: string | number = folderId;
            if (folderId < 0) {
                folder = EmailFolder[folder];
            }

            const path = generatePath(this.webviewList, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                folder,
            });
            let url = addPathForCurrentPortal(path);
            if (systemFilterType) {
                url = addQueryParams(url, { filter: systemFilterType.toString() });
            }
            return url;
        },
        print: getPathForAllPortals("/Message/Print/:messageId/:folderId"),
        getPrintLink(messageId: number, folderId: number) {
            return addPathForCurrentPortal(generatePath(this.print, { messageId, folderId }));
        },
        details: getPathForAllPortals("/Message/:folder/:messageId"),
        getMessageDetailsLink(messageId: number, folderId?: number) {
            let folder: string | number | undefined = folderId;
            if (folderId && folderId < 0 && folderId in EmailFolder) {
                folder = EmailFolder[folderId];
            } else if (folder === undefined) {
                folder = EmailFolder[EmailFolder.Inbox];
            }

            const path = generatePath(this.details, { folder, messageId });
            return addPathForCurrentPortal(path);
        },
        webviewPrint: getPathForWebviewPage("/Message/Print/:messageId/:folderId"),
        getWebviewPrintLink(
            webviewConfig: IWebviewRouteParams,
            messageId: number,
            folderId: number,
        ) {
            const path = generatePath(this.webviewPrint, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                messageId,
                folderId,
            });
            return addPathForCurrentPortal(path);
        },
        webviewDetails: getPathForWebviewPage("/Message/:folder/:messageId"),
        getWebviewMessageDetailsLink(
            webviewConfig: IWebviewRouteParams,
            messageId: number,
            folderId?: number,
        ) {
            let folder: string | number | undefined = folderId;
            if (folderId && folderId < 0 && folderId in EmailFolder) {
                folder = EmailFolder[folderId];
            } else if (folder === undefined) {
                folder = EmailFolder[EmailFolder.Inbox];
            }

            const path = generatePath(this.webviewDetails, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                folder,
                messageId,
            });
            return addPathForCurrentPortal(path);
        },
        webviewCompose: getPathForWebviewPage("/Compose/:messageId/:jobId"),
        getWebviewComposeLink(
            webviewConfig: IWebviewRouteParams,
            messageId: number,
            jobId: number,
            optionalQsParams?: {
                replyType?: ReplyType;
                replyId?: number;
            },
        ) {
            const path = generatePath(this.webviewCompose, {
                platform: webviewConfig.platform.toLowerCase(),
                mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                messageId,
                jobId,
            });
            return stringifyUrl({
                url: addPathForCurrentPortal(path),
                query: {
                    replyType: optionalQsParams?.replyType?.toString(),
                    replyId: optionalQsParams?.replyId?.toString(),
                },
            });
        },
        detailsExternal: getPathForAllPortals("/External/Message/:shareToken"),
        getMessageDetailsExternalLink(shareToken: string) {
            return generatePath(this.detailsExternal, { shareToken });
        },
        moveMessage: "/Messages/Move",
        getMoveMessageLink() {
            return this.moveMessage;
        },

        compose: getPathForAllPortals("/Compose/:messageId/:jobId"),
        /**
         * Gets the link to a new message editor
         *
         * @param messageId - The message id to be edited (0 if editing new message instead of a draft)
         * @param jobId - The current jobId (0 if no Job has been selected yet)
         * @param optionalQsParams - Additional optional link params
         */
        getComposeLink(
            messageId: number,
            jobId: number,
            optionalQsParams?: {
                /** The reply type of the new message */
                replyType?: ReplyType;
                /** The ID of the message being replied to (0 if no reply) */
                replyId?: number;
                /** The ID of the recipient to whom you are sending the message */
                defaultRecipientId?: number;
                /** The subject of the message */
                defaultSubject?: string;
                /** The document instance ids that are attached to the message */
                attachedDocumentIds?: number[];
                /** Special case for mailing ownerProfile photo, which is currently not a doc inst */
                attachOwnerProfile?: boolean;
            },
        ) {
            const path = generatePath(this.compose, { messageId, jobId });
            return stringifyUrl({
                url: addPathForCurrentPortal(path),
                query: {
                    replyType: optionalQsParams?.replyType?.toString(),
                    replyId: optionalQsParams?.replyId?.toString(),
                    defaultRecipientId: optionalQsParams?.defaultRecipientId?.toString(),
                    defaultSubject: optionalQsParams?.defaultSubject,
                    docInstanceIds: optionalQsParams?.attachedDocumentIds?.join(","),
                    attachOwnerProfile: optionalQsParams?.attachOwnerProfile?.toString(),
                },
            });
        },
    },
    mediaFolderList: {
        getFolderList(folderId: number, jobId: number, mediaType: MediaType) {
            let location = "";
            const directory = jobId === 0 ? DirectoryType.GlobalDocs : DirectoryType.Standard;
            switch (mediaType) {
                case MediaType.Document:
                    location = routes.document.getListLink(directory, folderId, jobId);
                    break;
                case MediaType.Photo:
                    location = routes.photo.getListLink(directory, folderId, jobId);
                    break;
                case MediaType.Video:
                    location = routes.video.getListLink(directory, folderId, jobId);
                    break;
            }
            return location;
        },
    },
    featureFlags: {
        list: "/Admin/FeatureFlags",
        details: "/Admin/FeatureFlag/Details/:id",
        getDetailsLink(id: number) {
            return generatePath(this.details, { id });
        },
    },
    search: {
        btAdminUtility: "/Admin/SearchUtils",
        resultModalPrefix: "/SearchResult",
    },
    partners: {
        list: "/Admin/Partners",
        details: "/Admin/Partner/Details/:id",
        getDetailsLink(id: number) {
            return generatePath(this.details, { id });
        },
    },
    subscriptionItemSetup: {
        details: "/Admin/SubscriptionItemSetupUtility",
        getDetailsLink() {
            return this.details;
        },
    },
    cancellation: {
        cancelBuilder: "/Admin/Cancel/:builderId/:subscriptionId",
        getCancellationLink(builderId: number, subscriptionId: string) {
            return generatePath(this.cancelBuilder, { builderId, subscriptionId });
        },
    },
    suspension: {
        suspendBuilder: "/Admin/Suspend/:builderId/:adminId/:editSuspension",
        getSuspensionLink(builderId: number, adminId: number, editSuspension: boolean) {
            return generatePath(this.suspendBuilder, { builderId, adminId, editSuspension });
        },
    },
    accountChange: {
        builderAccountChangeRequest: "/BuilderAccountChangeRequest/:builderId/:linkId",
        getBuilderAccountChangeRequestLink(builderId: number, linkId: string) {
            return generatePath(this.builderAccountChangeRequest, { builderId, linkId });
        },
    },
    rebateReceiptReview: {
        queues: "/Admin/RebateHawk/Queues",
        queue: "/Admin/RebateHawk/Queue/:queueId",
        getQueueLink(queueId: number) {
            return generatePath(this.queue, { queueId });
        },
        queueReceiptDetail: "/Admin/RebateHawk/Queue/:queueId/Receipt/:receiptId",
        getQueueReceiptDetailLink(queueId: number, receiptId: number) {
            return generatePath(this.queueReceiptDetail, { queueId, receiptId });
        },
        list: "/Admin/RebateHawk/Receipts",
        receiptDetail: "/Admin/RebateHawk/Receipts/:receiptId",
        getReceiptDetailLink(receiptId: number) {
            return generatePath(this.receiptDetail, { receiptId });
        },
    },
    login: {
        list: "/Login",
        passwordEntry: "/Login/Password",
        standaloneFrame: "/Login/Frame",
        getListLink() {
            return this.list;
        },
        getPasswordEntryLink() {
            return this.passwordEntry;
        },
        getStandaloneFrameLink() {
            return this.standaloneFrame;
        },
    },
    logout: {
        logoutLink: "/api/logout",
        getLogoutLink() {
            return this.logoutLink;
        },
        getLogoutAndRedirectLink(redirectUrl: string) {
            return `${this.logoutLink}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
        },
    },
    accounting: {
        // TODO: Clean up old path and rename these to "settings" after the full page experience is released
        accountingSettings: "/AccountingSettings",

        getAccountingSettingsLink() {
            return `Settings${this.accountingSettings}`;
        },
        sageIntacctConnect: "/AccountingSettings/SageIntacct/Connect",
        getSageIntacctConnectLink() {
            return `Settings${this.sageIntacctConnect}`;
        },
        sageIntacctEntityLinks: "/AccountingSettings/SageIntacct/EntityLinks/:entityType",
        getMassLinkToAccountingLink(entityType: number) {
            return generatePath(routes.settings.settings + this.sageIntacctEntityLinks, {
                entityType,
            });
        },

        settings: "/Settings/Accounting",
        getSettingsLink() {
            return this.settings;
        },

        linkEntityToAccounting: {
            details: "/LinkEntityToAccounting/:idType/:entityID?",
            getDetailsLink(idType: IDTypes, entityID?: number) {
                return generatePath(this.details, { idType, entityID });
            },
        },
        otherAccountingCost: {
            details: "/OtherAccountingCost/:id/:transactionId?",
            getDetailsLink(otherAccountingCostId: number, transactionId?: string) {
                return generatePath(this.details, {
                    id: otherAccountingCostId,
                    transactionId: transactionId,
                });
            },
        },
        oAuthRedirect: "/Settings/Accounting/OAuthRedirect",
        quickBooksSyncSchedule: "/QuickBooksSyncSchedule",
        massImportFromAccounting: {
            path: "/MassImportFromAccounting/:entityType",
            getMassImportFromAccountingLink(entityType: number) {
                return generatePath(this.path, {
                    entityType: entityType,
                });
            },
        },
        massLinkToAccounting: {
            path: "/MassLinkToAccounting/:entityType",
            getMassLinkToAccountingLink(entityType: number) {
                const path = generatePath(this.path, {
                    entityType: entityType,
                });
                return addPathForCurrentPortal(path);
            },
        },
    },
    payroll: {
        settings: "/PayrollSettings/:success?/:error?",
        getSettingsLink(success?: string, error?: string) {
            return generatePath(this.settings, { success, error });
        },
    },
    bluebeam: {
        settings: "/BluebeamSettings",
        getSettingsLink() {
            return this.settings;
        },
    },
    oauth: {
        authRedirect: "/OAuth/AuthRedirect/:code?/:state?",
        getAuthRedirectLink(code: string, state: string) {
            return generatePath(this.authRedirect, { code, state });
        },
    },
    signatureRequest: {
        details: "/SignatureRequest/:id/:jobId",
        getDetailsLink(id: number, jobId: number) {
            return generatePath(this.details, { id, jobId });
        },
        create: "/CreateSignatureRequest/:documentInstanceId/:jobId",
        getCreateLink(documentInstanceId: number, jobId: number) {
            return generatePath(this.create, { documentInstanceId, jobId });
        },
        remove: "/RemoveSignatureRequest/:removeAll",
        getRemoveLink(removeAll: boolean) {
            return generatePath(this.remove, { removeAll });
        },
        sign: "/SignatureSigning/:documentInstanceId/:loginType/:builderId",
        getSignLink(documentInstanceId: number, loginType: BTLoginTypes, builderId: number) {
            return generatePath(this.sign, { documentInstanceId, loginType, builderId });
        },
        standaloneDetails: "/SignatureRequest/:id/:jobId/:docInstanceId",
        getStandaloneDetailsLink(id: number, jobId: number, docInstanceId: number) {
            return generatePath(this.standaloneDetails, { id, jobId, docInstanceId });
        },
        webviewSign: getPathForWebviewPage("/SignDocument/:docInstanceId"),
        getWebviewSignLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewSign, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
        webviewSignatureRequest: getPathForWebviewPage("/SignatureRequest/:docInstanceId"),
        getWebviewSignatureRequestLink(webviewConfig: IWebviewRouteParams) {
            return addPathForCurrentPortal(
                generatePath(this.webviewSignatureRequest, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );
        },
    },
    signature: {
        details: getPathForAllPortals("/signature/:signatureId/:jobId"),
        getDetailsLink(signatureId: number, jobId: number) {
            const path = generatePath(this.details, { signatureId, jobId });
            return addPathForCurrentPortal(path);
        },
        detailsWebView: getPathForWebviewPage("/signature/:signatureId/:jobId"),
        external: "/:entityName/signature",
        getPathForExternalLink(entityShareToken: string, entityName: string) {
            return generatePath(`${routes.share.path}${this.external}`, {
                shareToken: entityShareToken,
                entityName,
            });
        },
    },
    lineItemsToInvoice: {
        path: "/LineItemsToInvoice",
    },
    selectionInvoicingWizard: {
        path: "/SelectionInvoicingWizard",
    },
    existingOwnerInvoiceSelect: {
        path: "/ExistingOwnerInvoiceSelect",
    },
    addFromLineItemsToInvoice: {
        path: "/addFromLineItemsToInvoice/:lineItemType/:entityIds?",
        getLink(lineItemType: LineItemType | LineItemType[], entityIds?: number[]) {
            if (Array.isArray(lineItemType)) {
                return generatePath(this.path, {
                    lineItemType: lineItemType.join(","),
                    entityIds: entityIds?.join(","),
                });
            }
            return generatePath(this.path, { lineItemType, entityIds: entityIds?.join(",") });
        },
    },
    poMapping: {
        path: "/POMapping",
        getUrlWithQueryString(
            url: string,
            ids: number[],
            jobId: number,
            toExistingPo: boolean,
            isProposed?: boolean,
        ) {
            // Using query strings is not preferred and can mess with routing. In this case,
            // it's necessary because selecting a large number of selections will quickly
            // exceed the 2000 character url limit set in place by our .net routing
            return queryString.stringifyUrl({
                url,
                query: {
                    ids: ids.join(),
                    jobId: jobId.toString(),
                    toExistingPo: toExistingPo.toString(),
                    isProposed: isProposed?.toString(),
                },
            });
        },

        getChangeOrderPath() {
            return `${this.path}/ChangeOrder`;
        },
        getChangeOrderLink(changeOrderIds: number[], jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getChangeOrderPath(),
                changeOrderIds,
                jobId,
                toExistingPo,
            );
        },

        getBidPath() {
            return `${this.path}/Bid`;
        },
        getBidLink(bidPackageIds: number[], jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getBidPath(),
                bidPackageIds,
                jobId,
                toExistingPo,
            );
        },

        getEstimatePath() {
            return `${this.path}/Estimate`;
        },
        getEstimateLink(estimateIds: number[], jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getEstimatePath(),
                estimateIds,
                jobId,
                toExistingPo,
            );
        },

        getSelectionPath() {
            return `${this.path}/Selection`;
        },
        getSelectionLink(selectionIds: number[], jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getSelectionPath(),
                selectionIds,
                jobId,
                toExistingPo,
            );
        },

        getSelectionChoicePath() {
            return `${this.path}/SelectionChoice`;
        },
        getSelectionChoiceLink(selectionChoiceIds: number[], jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getSelectionChoicePath(),
                selectionChoiceIds,
                jobId,
                toExistingPo,
            );
        },

        getSelectionOptionPath() {
            return `${this.path}/SelectionOption`;
        },
        getSelectionOptionLink(selectionOptionId: number, jobId: number, toExistingPo: boolean) {
            return this.getUrlWithQueryString(
                this.getSelectionOptionPath(),
                [selectionOptionId],
                jobId,
                toExistingPo,
            );
        },
    },
    signupForm: {
        signup: "/Signups/:code/:autoCode?/:salesforceAccountID?/:salesforceOpportunityID?/:rewinBuilderID?/:sfRewinContactLoginID?/:testExperience?",
        getExternalLink(
            code: string,
            autoCode?: string,
            salesforceAccountID?: string,
            salesforceOpportunityID?: string,
            rewinBuilderID?: string,
            sfRewinContactLoginID?: string,
            testExperience?: string,
        ) {
            return generatePath(this.signup, {
                code,
                autoCode,
                salesforceAccountID,
                salesforceOpportunityID,
                rewinBuilderID,
                sfRewinContactLoginID,
                testExperience,
            });
        },
    },
    signupFormWithQuote: {
        signup: "/Signups/Pricing/:quoteID",
        getExternalLink(quoteID: string) {
            return generatePath(this.signup, {
                quoteID,
            });
        },
    },
    signupPriceCodePage: {
        signup: "/Signups",
        getSignupLink() {
            return generatePath(this.signup);
        },
    },
    signupWhatsNext: {
        whatsNext: "/Signups/WhatsNext/:builderId/:fromEmail/:isRewin/:loginId?",
        getWhatsNextLink(
            builderId: string,
            fromEmail: boolean,
            isRewin: boolean,
            loginId?: string,
        ) {
            return generatePath(this.whatsNext, { builderId, fromEmail, isRewin, loginId });
        },
    },

    excludeFromChurn: {
        excludeFromChurn: "/Admin/ExcludeFromChurn/:builderId",
        getExcludeFromChurnLink(builderId: number) {
            return generatePath(this.excludeFromChurn, { builderId });
        },
    },
    errorPage: {
        noAccess: "/Error/NoAccess/:reason",
        getNoAccessLink(reason: ForbiddenReason) {
            return generatePath(this.noAccess, { reason });
        },
        legacyWebformsServerError: "/Error/Server",
        error404: "/Error/404",
        getNotFoundLink() {
            return addPathForCurrentPortal(this.error404);
        },
    },
    ownerPortalRedirect: "/ownerPortalRedirect/:jobId/:editPermissions",
    getOwnerPortalRedirectLink(jobId: number, editPermissions: boolean = false) {
        return generatePath(this.ownerPortalRedirect, { jobId, editPermissions });
    },
    list: {
        selectedEntityCopy: "/Copy/:ids",
        getCopyLink(ids: number[]) {
            return generatePath(this.selectedEntityCopy, { ids: ids.join(",") });
        },
    },
    ownerSummary: {
        path: "/Owner/Summary", // todo combine with builder landing
        getLink() {
            return this.path;
        },
    },
    subSummary: {
        path: "/Subs/Summary",
        getLink() {
            return this.path;
        },
        recentCommentsRoute: "/RecentComments",
        rfiRelatedRoute: "/RfiRelated",
        billDetailRoute: "/BillDetail",
        lienWaiverDetailRoute: "/LienWaiverDetail",
    },
    ownerProjectUpdates: {
        webviewList: getPathForWebviewPage("/OwnerProjectUpdates"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            queryParams?: {
                jobId?: number;
            },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                }),
            );

            if (!queryParams) {
                return path;
            }

            const params = new URLSearchParams();
            if (queryParams.jobId) {
                params.append("jobId", queryParams.jobId.toString());
            }

            const queryString = params.toString();
            return queryString ? `${path}?${queryString}` : path;
        },
    },
    lowes: {
        lowesSettings: "/LowesSettings",
        getLowesSettingsLink() {
            return this.lowesSettings;
        },
    },
    vendor: {
        homeDepotSettings: "/TheHomeDepotSettings",
        getHomeDepotSettingsLink() {
            return this.homeDepotSettings;
        },
        vendorStoreFinder: "/VendorStoreFinder/:vendorType",
        getVendorStoreFinderLink(vendorType: VendorTypes) {
            return generatePath("/VendorStoreFinder/:vendorType", { vendorType });
        },
        vendorReceipt: getPathForExternalPrint("/VendorReceipt/:vendorType/:vendorPurchaseId"),
    },
    tradeAgreement: {
        subTradeAgreement: getPathForBuilderOrSubPortals("/TradeAgreement/:builderId/:subId"),
        getSubTradeAgreementLink(builderId: number, subId: number) {
            return generatePath("/TradeAgreement/:builderId/:subId", { builderId, subId });
        },
        print: getPathForAllPortals("/TradeAgreement/Print/:tradeAgreementId"),
        getSubTradeAgreementPrintLink(tradeAgreementId: number) {
            return addPathForCurrentPortal(
                generatePath("/TradeAgreement/Print/:tradeAgreementId", { tradeAgreementId }),
            );
        },
    },
    landingPage: {
        path: "/Landing",
        getLink() {
            return this.path;
        },
        getLinkForLoginType(loginType: BTLoginTypes, includeBaseRoute: boolean) {
            let url = "";
            if (loginType === BTLoginTypes.BUILDER) {
                url = this.path;
            } else if (loginType === BTLoginTypes.SUBS) {
                url = routes.subSummary.getLink();
            } else if (loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT) {
                url = routes.ownerSummary.getLink();
            }

            if (includeBaseRoute) {
                url = getBaseRoute() + url;
            }
            return url;
        },
    },
    tax: {
        settings: "/TaxesSettings",
        getSettingsLink() {
            return this.settings;
        },
        taxRateDetails: "/Tax/TaxRate/:taxRateId",
        getTaxRateDetailsLink(taxRateId: number) {
            return generatePath(this.taxRateDetails, { taxRateId });
        },
        taxGroupDetails: "/Tax/TaxGroup/:taxGroupId",
        getTaxGroupDetailsLink(taxGroupId: number) {
            return generatePath(this.taxGroupDetails, { taxGroupId });
        },
    },
    share: {
        path: "/share/:shareToken",
        getLink(shareToken: string, referralBy?: number) {
            return generatePath(this.path, { shareToken, referralBy });
        },
    },
    assembly: {
        details: "/assembly/:assemblyId",
        getAssemblyDetails(assemblyId: number) {
            return generatePath(this.details, { assemblyId });
        },
    },
    workdayException: {
        details: "/:orgId/WorkdayException/:workdayExceptionID/:jobID/:isOffline",
        getWorkdayExceptionDetailsLink(
            orgId: number,
            workdayExceptionID: number,
            jobID: number,
            isOffline: boolean,
        ) {
            return generatePath(this.details, { orgId, workdayExceptionID, jobID, isOffline });
        },
    },
    sentry: {
        adminLogExceptionInSentry: "/Admin/LogExceptionInSentry",
    },
    help: {
        giveFeedback: "/Help/Givefeedback",
        getHelpCenterUrl: (type: HelpCenterTypes) => {
            // The Salesforce Service Cloud help center (/s/ paths) mirrors most
            // articles to the test subdomain, so on any non-prod host we
            // resolve them there. Exceptions are hardcoded to prod because
            // their test equivalents don't exist: Sage 404s on test, and the
            // legacy help center pages (/en/articles/) are login-walled and
            // unconfirmed. Chase is special — the same article is published
            // under a different slug on each host — so it selects both host
            // and slug below.
            const helpHost =
                getCurrentEnvironment() !== "prod"
                    ? "https://helpcenter.test.buildertrend.net"
                    : "https://helpcenter.buildertrend.net";
            switch (type) {
                // Service Cloud (/s/) pages — resolved per host; test versions exist
                case HelpCenterTypes.HelpCenter:
                    return helpHost;
                case HelpCenterTypes.SupportedBrowsers:
                    return `${helpHost}/s/article/Buildertrend-Setup-Customization-Faqs`;
                case HelpCenterTypes.PaymentsFromPrintouts:
                    return `${helpHost}/s/article/Financial-Management-Settings`;
                case HelpCenterTypes.SubHelpCenter:
                    return `${helpHost}/s/topic/0TOPB0000003b6T4AQ/subcontractor-help-center`;
                case HelpCenterTypes.OwnerHelpCenter:
                    return `${helpHost}/s/topic/0TOPB0000003b6N4AQ/client-help-center`;
                case HelpCenterTypes.QuickBooksHelp:
                    return `${helpHost}/s/article/Advanced-Quickbooks-Online-Integration-Overview`;
                case HelpCenterTypes.QuickBooksDesktopHelp:
                    return `${helpHost}/s/article/Advanced-Quickbooks-Desktop-Integration-Overview`;
                case HelpCenterTypes.XeroHelp:
                    return `${helpHost}/s/article/Xero-Initial-Integration`;
                case HelpCenterTypes.CalendarFeedGuide:
                    return `${helpHost}/s/article/Project-Management-Settings`;
                case HelpCenterTypes.Leads:
                    return `${helpHost}/s/article/Lead-Opportunities-Overview`;
                case HelpCenterTypes.Bluebeam:
                    return `${helpHost}/s/article/Bluebeam`;
                case HelpCenterTypes.NewSelectionsExperience:
                    return `${helpHost}/s/article/Selections`;
                // Same article, but published under a different slug per host
                case HelpCenterTypes.ChaseBankAccounts:
                    return getCurrentEnvironment() !== "prod"
                        ? "https://helpcenter.test.buildertrend.net/s/article/Buildertrend-Payments-Paying-Subcontractors"
                        : "https://helpcenter.buildertrend.net/s/article/Buildertrend-Payments-Bill-Pay";
                // Prod-only: no working test equivalent
                case HelpCenterTypes.SageIntacct:
                    return "https://helpcenter.buildertrend.net/s/article/Sage";
                case HelpCenterTypes.NotificationConfigOptions:
                    return "https://helpcenter.buildertrend.net/en/articles/4855946-notification-configuration-options";
                case HelpCenterTypes.SubNotificationConfigOptions:
                    return "https://helpcenter.buildertrend.net/en/articles/5174202-subcontractor-notifications";
                case HelpCenterTypes.OwnerNotificationConfigOptions:
                    return "https://helpcenter.buildertrend.net/en/articles/5174208-homeowner-notifications";
                case HelpCenterTypes.ThreeDs:
                    return "https://helpcenter.buildertrend.net/en/articles/9235515-3d-secure-3ds-homeowner-faqs";
                default:
                    return helpHost;
            }
        },
        getHelpCenterUrlForLoginType(loginType: BTLoginTypes) {
            switch (loginType) {
                case BTLoginTypes.BUILDER:
                    return this.getHelpCenterUrl(HelpCenterTypes.HelpCenter);
                case BTLoginTypes.SUBS:
                    return this.getHelpCenterUrl(HelpCenterTypes.SubHelpCenter);
                case BTLoginTypes.OWNER:
                case BTLoginTypes.CONTACT:
                    return this.getHelpCenterUrl(HelpCenterTypes.OwnerHelpCenter);
                default:
                    // Preserve legacy empty-string fallback for unknown login types
                    return "";
            }
        },
    },
    activityFeed: {
        path: "/ActivityFeed",
    },
    notConnected: {
        path: "/Owner/AccountNotConnected/:fromLoginPage",
        getLink(fromLoginPage: boolean) {
            return generatePath(this.path, { fromLoginPage });
        },
    },
    subscriptionInactive: {
        path: "/SubscriptionInactive",
        getLink() {
            return this.path;
        },
    },
    entityLink: {
        path: "/link/:externalId",
        getLink(externalId: string) {
            return generatePath(this.path, { externalId });
        },
        getFullLink(externalId: string) {
            return `${window.location.origin}/app${this.getLink(externalId)}`;
        },
        // Used to test if pasted text is a /share link. We want to be as strict as possible with
        // this filtering to prevent a non-BT share link from showing as a rich embed in the editor.
        parsePath: (pathname: string) => {
            const result = matchPath<{ externalId: string }>(pathname, {
                path: ["**/link/:externalId"],
                exact: false,
                strict: false,
            });
            if (!result?.params.externalId) return null;
            const externalIdRegex = /[a-fA-F0-9]{8}(-[a-fA-F0-9]{4}){3}-[a-fA-F0-9]{12}/g;
            if (!externalIdRegex.test(result.params.externalId)) return null;
            return {
                externalId: result.params.externalId,
            };
        },
    },
    jobCostingBudget: {
        path: getPathForAllPortals("/JobCostingBudget"),
        getLink: () => addPathForCurrentPortal(`/JobCostingBudget`),
        webview: getPathForWebviewPage("/JobCostingBudget"),
        contractedProfitDetails: getPathForAllPortals("/ContractedProfit/:jobId"),
        getContractedProfitDetails(jobId: number) {
            return addPathForCurrentPortal(`/ContractedProfit/${jobId}`);
        },
    },
    reporting: {
        reportList: {
            path: "/Reporting",
            getLink: () => addPathForCurrentPortal(`/Reporting`),
            webview: getPathForWebviewPage("/Reporting"),
        },
        WIPReport: {
            path: "/Reporting/WIPReport/:openJobLimit?",
            getLink(openJobLimit?: number) {
                return addPathForCurrentPortal(generatePath(this.path, { openJobLimit }));
            },
            webview: getPathForWebviewPage("/Reporting/WIPReport/:openJobLimit?"),
        },
        ProfitabilityReport: {
            path: "/Reporting/ProfitabilityReport/:openJobLimit?",
            getLink(openJobLimit?: number) {
                return addPathForCurrentPortal(generatePath(this.path, { openJobLimit }));
            },
            webview: getPathForWebviewPage("/Reporting/ProfitabilityReport"),
        },
        InvoicingReport: {
            path: "/Reporting/InvoicingReport/:openJobLimit?",
            getLink(openJobLimit?: number) {
                return addPathForCurrentPortal(generatePath(this.path, { openJobLimit }));
            },
            webview: getPathForWebviewPage("/Reporting/InvoicingReport"),
        },
        BudgetedVsProjected: {
            path: "/Reporting/BudgetedVsProjected/:openJobLimit?",
            getLink(openJobLimit?: number) {
                return addPathForCurrentPortal(generatePath(this.path, { openJobLimit }));
            },
            webview: getPathForWebviewPage("/Reporting/BudgetedVsProjected/:openJobLimit?"),
        },
        HoursByJobReport: {
            path: "/Reporting/HoursByJobReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/HoursByJobReport"),
        },
        HoursByEmployeeReport: {
            path: "/Reporting/HoursByEmployeeReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/HoursByEmployeeReport"),
        },
        SchedulePercentCompleteByJob: {
            path: "/Reporting/SchedulePercentCompleteByJob/",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/SchedulePercentCompleteByJob"),
        },
        LaborActualsVsBudgetedReport: {
            path: "/Reporting/LaborActualsVsBudgetedReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/LaborActualsVsBudgetedReport"),
        },
        DailyLogCreationByJobReport: {
            path: "/Reporting/DailyLogCreationByJobReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/DailyLogCreationByJobReport"),
        },
        DailyLogsByUserReport: {
            path: "/Reporting/DailyLogsByUserReport/",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/DailyLogsByUserReport"),
        },
        CashflowReport: {
            path: "/Reporting/CashflowReport",
            getLink() {
                return addPathForCurrentPortal(generatePath(this.path));
            },
            webview: getPathForWebviewPage("/Reporting/CashflowReport"),
        },
        LeadActivitiesBySalespersonReport: {
            path: "/Reporting/LeadActivitiesBySalespersonReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/LeadActivitiesBySalespersonReport"),
        },
        LeadStatusBySourceReport: {
            path: "/Reporting/LeadStatusBySourceReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/LeadStatusBySourceReport"),
        },
        ChangeOrderProfitReport: {
            path: "/Reporting/ChangeOrderProfitReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/ChangeOrderProfitReport"),
        },
        BaselineVsActualDurationReport: {
            path: "/Reporting/BaselineVsActualDurationReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/BaselineVsActualDurationReport"),
        },
        LeadCountBySalespersonReport: {
            path: "/Reporting/LeadCountBySalespersonReport",
            getLink() {
                return generatePath(this.path);
            },
            webview: getPathForWebviewPage("/Reporting/LeadCountBySalespersonReport"),
        },
    },
    task: {
        list: getPathForAllPortals("/tasks/:viewId?"),
        external: "/tasks/external",
        externalDetails: "/Tasks/external/task/:externalId",
        getExternalLink(shareToken: string) {
            return `${routes.share.getLink(shareToken)}${this.external}`;
        },
        getListLink(viewId?: string, filter?: TaskFilterPresetKey) {
            let url: string;
            if (viewId) {
                url = addPathForCurrentPortal(generatePath("/tasks/:viewId", { viewId }));
            } else {
                url = addPathForCurrentPortal("/tasks");
            }
            if (filter) {
                url += `?filter=${encodeURIComponent(filter)}`;
            }
            return url;
        },
        getMyTasksLink(loginType: BTLoginTypes) {
            let portalType = PortalType.BUILDER;
            if (loginType === BTLoginTypes.SUBS) {
                portalType = PortalType.SUBS;
            } else if (loginType === BTLoginTypes.OWNER || loginType === BTLoginTypes.CONTACT) {
                portalType = PortalType.OWNER;
            }
            return addPathForPortal("/tasks/my-tasks", portalType);
        },
        singlePrint: getPathForAllPortals("/tasks/print/:externalId"),
        getSinglePrintLink(externalId: string) {
            return addPathForCurrentPortal(generatePath(this.singlePrint, { externalId }));
        },
        bulkPrint: getPathForAllPortals("/tasks/BulkPrint/:shareToken"),
        getBulkPrintLink(shareToken: string) {
            return addPathForCurrentPortal(generatePath(this.bulkPrint, { shareToken }));
        },
        getGanttLink() {
            return `${addPathForCurrentPortal("/tasks")}?view=gantt`;
        },
        details: getPathForAllPortals("/tasks/:viewId?/task/:externalId"),
        getDetailsLink(externalId: string, viewId?: string) {
            return addPathForCurrentPortal(
                generatePath("/tasks/:viewId?/task/:externalId", {
                    viewId,
                    externalId,
                }),
            );
        },
        viewSettings: getPathForAllPortals("/tasks/:viewId?/viewSettings"),
        getViewSettingsLink(viewId?: string) {
            return addPathForCurrentPortal(
                viewId
                    ? generatePath("/tasks/:viewId/viewSettings", { viewId })
                    : "/tasks/viewSettings",
            );
        },
        getExternalDetailsLink(shareToken: string, externalId: string) {
            return generatePath(routes.share.path + this.externalDetails, {
                shareToken,
                externalId,
            });
        },
        externalPrint: "/tasks/external/print/:externalId",
        getExternalPrintLink(shareToken: string, externalId: string) {
            return generatePath(routes.share.path + this.externalPrint, {
                shareToken,
                externalId,
            });
        },
        webviewList: getPathForWebviewPage("/:interfaceVersion/tasks/:viewId?"),
        getWebviewListLink(
            webviewConfig: IWebviewRouteParams,
            interfaceVersion: string,
            queryParams?: {
                jobIds?: string;
                new?: boolean;
                viewId?: string;
            },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewList, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    interfaceVersion,
                    viewId: queryParams?.viewId,
                }),
            );

            if (!queryParams) {
                return path;
            }

            const params = new URLSearchParams();
            if (queryParams.jobIds) {
                params.append("jobIds", queryParams.jobIds);
            }
            if (queryParams.new) {
                params.append("new", "true");
            }

            const queryString = params.toString();
            return queryString ? `${path}?${queryString}` : path;
        },
        webviewViewSettings: getPathForWebviewPage(
            "/:interfaceVersion/tasks/:viewId?/viewSettings",
        ),
        getWebviewViewSettingsLink(
            webviewConfig: IWebviewRouteParams,
            interfaceVersion: string,
            viewId?: string,
        ) {
            return addPathForCurrentPortal(
                generatePath(this.webviewViewSettings, {
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    interfaceVersion,
                    viewId,
                }),
            );
        },
        webviewDetails: getPathForWebviewPage("/:interfaceVersion/tasks/:viewId?/task/:externalId"),
        getWebviewDetailsLink(
            externalId: string,
            webviewConfig: IWebviewRouteParams,
            interfaceVersion: string,
            queryParams?: {
                jobIds?: string;
                new?: boolean;
                viewId?: string;
            },
        ) {
            const path = addPathForCurrentPortal(
                generatePath(this.webviewDetails, {
                    externalId,
                    platform: webviewConfig.platform.toLowerCase(),
                    mobileVersion: webviewConfig.mobileVersion.toLowerCase(),
                    interfaceVersion,
                    viewId: queryParams?.viewId,
                }),
            );

            if (!queryParams) {
                return path;
            }

            const params = new URLSearchParams();
            if (queryParams.jobIds) {
                params.append("jobIds", queryParams.jobIds);
            }
            if (queryParams.new) {
                params.append("new", "true");
            }

            const queryString = params.toString();
            return queryString ? `${path}?${queryString}` : path;
        },
    },
} as const;
